// SongBuzz Solo, whichever way it's drawn: the Tour and the Daily Gig on one phone. The rules are in solo-core.ts; this
// is everything else that isn't drawing: it brings the songs, plays them (WebAudio, so Legends can twist them), keeps
// your progress, and decides what happens next, in what order. The site's page (src/pages/solo.ts) and the app's screens
// (app/src/solo) each give it their view: soloGame(view) calls view.draw('menu', data) for a screen, and a hook for each
// moment inside one (view.clock, view.tallyStep...). The view reads the state and asks for what it needs from what this
// returns; a tap on the screen comes back through act(name, data).
import * as SongBuzzEngine from '../core/engine.ts';
import '../core/solo-i18n.ts';
import { ACCOUNT, LANG, SB, avatar, catalogueSongs, countryName, decadeText, detectCountry, deviceId, store, t, track, whenShown } from './common.ts';
import './solo-icons.ts';
import { dayBefore, dayGo as dayGoOf, dayNo, mine as mineOf, passHolds as passHoldsOf, streakOf, today, tomorrow, untilTomorrow, yesterday } from './daily.ts';
import { keepWeek } from './daily-legend.ts';
import { dailyFriendsBoard, dailyReminder, pushCallsAs, pushNeedsPin, remindDaily } from './push.ts';
import { pinAdd } from './pin.ts';
import { soloEffects } from './sfx.ts';
import { ITEMS, hasUnlocked, progressOf } from '../core/wardrobe.ts';
import type { OutfitSlot } from '../core/wardrobe.ts';
import type { SoloSound } from './sfx.ts';
import * as SoloCore from '../core/solo-core.ts';
import type { AnswerOutcome, Armed, BetResult, Cleared, CrateDef, CurrentSong, DailyGo, DealPlan, DailyStanding, DuelBy, DuelRecord, Ghost, Gig, GigEnd, GigLegend, HandCard, HistoryEntry, LegendDef, Meta, Moment, Prize, Run, RunMode, ScoreStep, SetId, Shop, ShopKind, ShopSticker, ShopTape, Song, SoundFx, Sticker, StickerDef, SyncedMeta, TapeDef, TimelineCard, Unlock } from '../core/solo-core.ts';
import type { LyricPuzzle, Pool, Similar, SongInfo, SongLike } from '../core/types.ts';
import type { ClientConfig, IconName, Mood, TrackDetails } from './common.ts';
import type { LegendWords } from './daily.ts';
import type { RebaseSession } from '/vendor/rebase-client.js';

// ---------------------------------------------------------------- Solo's own shapes
export type SongId = Song['id'];
// A song as the deck holds it: what the catalogue or a playlist gave, and what the page has found out since.
export interface DeckSong extends Song { noYear?: boolean; hint?: string }
export interface DeckSave { queue: DeckSong[]; all: DeckSong[] }
// What's kept on the device: a run with its deck, and whose progress this device holds (sb_solo_sync).
export interface SavedRun { run: Run; deck: DeckSave }
export interface SyncHeld { by?: string; rev?: number }
// The song on, as saved: restarted, it has had its one free go again (see resume).
export type SongOn = CurrentSong & { restarted?: boolean };
// An answer on the play screen: a song, or the next line of a lyric.
export interface AnswerOption { title?: string; artist?: string; line?: string; right?: boolean }
// How a song plays: its twist, where it starts and stops, and what's next when it ends.
export interface PlayFx extends SoundFx { from?: number; stopAt?: number; onEnd?: () => void }
export interface Playing { src: AudioBufferSourceNode; gain: GainNode; extra: AudioBufferSourceNode[]; id: SongId }
export type SfxName = SoloSound;
export type HapticKind = 'light' | 'success' | 'error';
// What the page asks of the phone app.
export type AppMessage = { type: 'google-signin'; webClientId: string } | { type: 'google-signout' } | { type: 'daily-done' | 'remind'; streak: number; n?: number; legend?: LegendWords | null };
// A challenge: done once test says so. It reads the run (null on the menu) and your progress.
export interface Challenge { id: string; icon: string; name: string; desc: string; fans: number; test: (r: Run | null, m: Meta) => unknown }
// A year bet's outcome, or no bet at all: the points just go in.
export type BetShown = BetResult | { verdict: 'bank'; hype: number; year?: null };
// The tip open on screen, and how a sticker in the case behaves when tapped.
export type TipFor = { kind: 'tapes' } | { kind: 'sticker' | 'tape'; i: number };
export interface CaseOpts { act?: string }
// What an event lands on: an element, usually, but the document has no closest().
export type Landed = EventTarget & Partial<Pick<Element, 'closest'>>;

// What the server answers (cloud/backend/functions/songbuzz.ts).
export type Json = Record<string, unknown>;
export interface Reply<R> { ok: boolean; status: number; j: R }
export interface SaveReply { meta?: Partial<SyncedMeta> | null; rev: number }
export type BoardKind = 'daily' | 'duel';
export interface BoardRow { name: string | null; look: string; hype: number; songs: number; rights: number; streak?: number; me?: boolean }
export interface BoardMe extends BoardRow { rank: number | null; unranked?: boolean }
export interface Board { n: number; top: BoardRow[]; me: BoardMe | null; streak: { n: number; last: string; pass: number } | null; ghost: Ghost | null; you: string; ranked?: boolean; signedIn?: boolean }
export interface DuelInfo extends Board { id: string; by: DuelBy; at: string }
// Who you're playing a challenge against, from its link or because you just made it.
export interface DuelStart { by?: DuelBy | null; ghost?: Ghost | null; mine?: boolean }
export interface DailyScore extends DailyStanding { best: number; hype: number; ranked: boolean; board: Board }
// Starting a go: a ticket, or the go was used.
export interface DailyUsed { already: true; token?: undefined; finished: boolean; hype: number; moved?: boolean }
export type DailyTicket = { token: string; ranked: boolean; already?: undefined } | DailyUsed;
export type DuelTicket = { token: string; already?: undefined } | { already: true; token?: undefined; hype: number };
// The account's client, and a session as the page reads it: it falls back to user.name, which Rebase's User doesn't declare.
export type Rebase = ReturnType<typeof ACCOUNT.client>;
export type Account = RebaseSession & { user: { name?: string | null } };

// The screen on show, told apart by `screen`. Each screen has its own fields.
export interface PlayUi { screen: 'play'; cur: CurrentSong; opts: AnswerOption[] | null; started: number; answered: boolean; revealed: boolean; revealedAt?: number; buzzAt: number | null; lead: number; lyric: LyricPuzzle | null; key: string; fiftyHidden?: number[]; wrongs?: number[]; named?: string | null }
export interface ResultUi { screen: 'result'; cur: CurrentSong; res?: AnswerOutcome; counting?: boolean; ff?: boolean; betDone?: boolean; guess?: number | null; settled?: boolean; outcome?: GigEnd; verdict?: GigEnd }
export interface CratesUi { screen: 'crates'; stake: number }
export interface HowtoUi { screen: 'howto'; back: string }
export interface DuelUi { screen: 'duel'; id: string; info?: DuelInfo }
export interface ErrorUi { screen: 'error'; retry: () => unknown }
export interface PlainUi { screen: 'menu' | 'loading' | 'paused' | 'board' | 'dailygate' | 'shelf' | 'profile' | 'starter' | 'intro' | 'hand' | 'cleared' | 'shop' | 'over' }
export type AnyUi = PlayUi | ResultUi | CratesUi | HowtoUi | DuelUi | ErrorUi | PlainUi;
// The code often looks at a field without first checking the screen: on any other screen it's simply undefined.
export type UiKey<U = AnyUi> = U extends unknown ? keyof U : never;
export type Complete<U> = U extends unknown ? U & { [K in Exclude<UiKey, keyof U>]?: undefined } : never;
export type Ui = Complete<AnyUi>;
export type ScreenName = Ui['screen'];

// ---------------------------------------------------------------- how the game talks to whatever draws it
type NoData = Record<never, never>;
type NightInfo = ReturnType<typeof SoloCore.nightInfo>;
export type LegendInfo = ReturnType<typeof SoloCore.legendFor>;
export interface Meter { now: number; of: number | null; pct: number; full: boolean; label: string }
// short: what it says when the row is tight (a phone with three of them)
export interface Badge { cls?: string; flame?: boolean; icon: string; title?: string; text: string; short?: string }
// the gig's status, for the head-up display
export interface Hud { M: Meter; where: string; songNo: number; ofSongs: number; reached: boolean; danger: boolean; skipsLine: string; badges: Badge[] }
export interface TapeHint { i: number; d: TapeDef; more: number }
export interface LastNight { trophy?: boolean; text: string }
export interface MenuNews { standing?: string; yest?: LastNight | null; streak?: number }
export interface Ear { best: string; bestPct: number; rows: { d: string; pct: number; color: string }[] }
export interface ShelfSong { id: string; title: string; artist: string; cover: string | null; year: number | null; n: number }
export interface TourNode { st: string; ic: string; legend: boolean; label: string; color?: string | null; set?: string | null } // color, set: the set it played, when not Name that song
export interface TourNight { n: number; N: NightInfo; here: boolean; past: boolean; nodes: TourNode[] }
export type SetlistEntry = HistoryEntry & { isNew: boolean };
// A screen, with what it needs to draw itself. The rest it reads from the game (run, ui, meta).
export interface Screens {
  loading: { text: string };
  error: { msg: string };
  paused: NoData;
  dailygate: { day: string };
  starter: { ch: Sticker[] };
  legend: { g: Gig; N: NightInfo; L: GigLegend; daily: boolean; twists: LegendDef[] };
  intro: { g: Gig; N: NightInfo; daily: boolean; coach: 'intro' | 'daily' | 'sets' | null; first: boolean; offers: OfferShow[] | null; set: SetShow | null };
  hand: { g: Gig; opening: boolean };
  play: NoData;
  missed: { song: Song; verdict: string; lost: string; glass: boolean; hud: Hud };
  tally: { song: Song; res: AnswerOutcome; hud: Hud };
  skippedSong: { song: Song; hud: Hud };
  cleared: { c: Cleared; seeded: boolean; spare: number; names: Record<string, string>; nextSlot: string };
  shop: { sh: Shop; nextNight: number; nextGig: number; nextTarget: number; full: boolean; LN: LegendInfo; showLegend: boolean };
  over: { won: boolean; daily: boolean; oneN: boolean; isDaily: boolean; isDuel: boolean; gained: number; newUnlocks: Unlock[]; looks: [slot: OutfitSlot, id: string][]; lv: number; nxt: number | undefined; prv: number; rights: number; title: string; where: string; why: string; gigsDone: number; keepTour: boolean; cont: { night: number; stickers: number } | null; dailyOpen: boolean };
  menu: { lv: number; next: number | null; prev: number; day: string; D: Meta['daily']; done: DailyGo | null; dlive: boolean | null; streak: number; shelfN: number; cont: Run | null };
  crates: { lv: number; cc: string; list: CrateDef[]; maxStake: number };
  board: { day: string; d: DailyGo | null };
  howto: { back: string; cards: string[][] };
  shelf: { by: Record<string, ShelfSong[]>; decs: string[]; n: number };
  profile: { lv: number; next: number | undefined; upcoming: Unlock[]; ear: Ear | null };
  duel: { id: string; info: DuelInfo; by: string; played: boolean };
}
// A sheet over the screen.
export interface Tips {
  account: { keep: boolean };
  offer: { kind: ShopKind; i: number; it: ShopSticker | ShopTape; d: StickerDef | TapeDef; tx: { name: string; desc: string }; full: boolean; poor: boolean };
  sticker: { i: number; s: Sticker; d: StickerDef; tx: { name: string; desc: string }; inShop: boolean };
  tape: { i: number; id: string; d: TapeDef; usable: boolean };
  tapes: { rows: { id: string; i: number; d: TapeDef; ok: boolean }[] };
  pause: { tour: boolean; today?: boolean };
  encore: NoData;
}
export interface ClockTick { part: number; low: boolean; speed: number; secs: number }
export interface TallyStep { st: ScoreStep; who: string; chips: number; mult: number; amt: string; label: string; cls: string }
export interface YearBet { res: AnswerOutcome; years: number[]; song: Song; seen: number | null; odds: string; clears: boolean; keep: string }
export interface TimelineBet { res: AnswerOutcome; cur: CurrentSong; tl: TimelineCard[]; ask: string; odds: string; seen: number | null; clears: boolean; keep: string }
export interface BetLine { kind: string; mark: IconName; head: string; sub: string; off: number }
export interface VerdictShow { win: boolean; tour: boolean; again?: boolean; where: string; text: string; seeded: boolean; hype: number; target: number; line: string; sub?: string; act: string; btn: string; pct: number }
// A set as the screens show it, a choice of one, and the crowd's moment
export interface SetShow { id: SetId; name: string; desc: string; color: string; icon: string; line: string }
export interface OfferShow extends SetShow { i: number; target: number; plays: number; prize: string; prizeIcon: string }
export interface MomentShow { id: Moment['id']; title: string; desc: string; icon: string }
export interface MoreKey { label: string; pts: string }
export interface SplashShow { g: Gig; seeded: boolean; N: NightInfo; set?: SetShow | null; moment?: MomentShow | null; color?: string }
// Everything the game asks of what draws it: draw a screen, and each moment inside one.
export interface SoloView {
  draw<K extends keyof Screens>(name: K, d: Screens[K]): void;
  tip<K extends keyof Tips>(kind: K, d: Tips[K]): void;
  closeTip(instant?: boolean): void;
  tipOpen(): boolean;
  field(color: string): void;
  bar(): void;
  toast(text: string, ms?: number, ic?: string): void;
  confetti(n?: number): void;
  google(): void;
  boards(): void;
  signing(on: boolean, b?: HTMLButtonElement): void;
  signedIn(): void;
  reminder(r: string): void;
  boardTab(k: string): void;
  menuNews(n: MenuNews): void;
  standing(text: string): void;
  nameSaved(): void;
  duelRow(d: DuelRecord): void;
  revealBar(f: number): void;
  revealed(buzz: boolean): void;
  clock(c: ClockTick): void;
  wrongOpt(i: number): void;
  answered(i: number, word: string): void;
  skipped(): void;
  tallyStep(s: TallyStep): void;
  tallyMult(m: number): void;
  tallySum(chips: number, m: number): void;
  tallyCharge(): void;
  tallyTotal(n: number): void;
  tallyLanded(big: boolean, hype: number): void;
  tallyCash(line: string): void;
  tallySpeed(): void;
  cash(): void;
  dare(): void;
  betYears(d: YearBet): void;
  betTimeline(d: TimelineBet): void;
  betBar(f: number): void;
  betMarked(r: BetResult, year: number | null): void;
  betLine(l: BetLine): void;
  betTotal(label: string | null, count: [number, number] | null): void;
  betFlash(kind: string, big: string, small?: string): void;
  hypeCount(from: number, to: number): void;
  hudIn(M: Meter): void;
  hud(): void;
  nextKey(label: string, act: string, failed: boolean): void;
  encoreFlash(): void;
  splitFlash(ahead: boolean, text: string): void;
  verdict(d: VerdictShow): void;
  verdictOpen(): boolean;
  rewards?(looks: [slot: OutfitSlot, id: string][]): void; // the whole screen for new things for your character (web/reward.ts)
  closeVerdict(): void;
  splash(d: SplashShow): void;
  clearedRow(k: number): void;
  clearedDone(): void;
  sleeves(): void;
  tapeUsed(): void;
  previewing(id: string | null): void;
}
// What a tap carries: from the page, an element's data-* (strings); from the app, the values.
export interface ActData { i?: number; id?: string; k?: string; kind?: string; cc?: string; slot?: string | number; y?: string | number; score?: string | number; from?: string }

export function soloGame(view: SoloView) {
  const refreshBar = () => view.bar();
  const toast = (text: string, ms?: number, ic?: string) => view.toast(text, ms, ic);
  const confetti = (n?: number) => view.confetti(n);
  const setField = (color: string) => view.field(color);
  const refreshBoards = () => view.boards();
  const C = SoloCore;
  const api = (p: string) => (SB.api || '') + p;
  const getJson = async <R>(url: string, ms = 9000): Promise<R> => {
    const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = ctl && setTimeout(() => ctl.abort(), ms);
    try { const r = await fetch(url, ctl ? { signal: ctl.signal } : {}); const j: R & { error?: string } = await r.json().catch(() => ({})); if (!r.ok) throw new Error(j.error || 'HTTP ' + r.status); return j; }
    finally { if (timer) clearTimeout(timer); }
  };
  const fmt = (n: number) => Math.round(n).toLocaleString(LANG === 'es' ? 'es-ES' : 'en-US');
  const money = (n: number) => '$' + n;
  const cloud = SB.mode === 'cloud';
  const T = (k: string, p?: TrackDetails) => { if (cloud && typeof track === 'function') track(k, p); };
  // where the run is, for an event about it (the tour's crate, night and gig; the Daily's and a challenge's are fixed)
  const runAt = () => (run ? { mode: run.mode, crate: run.crate, night: run.night + 1, gig: run.gigNo + 1, stake: run.stake } : {});
  const reduced = () => (window.matchMedia as Window['matchMedia'] | undefined) && matchMedia('(prefers-reduced-motion: reduce)').matches;
  const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
  // the app (react-native-webview) gives a little buzz in the hand; browsers that can, vibrate
  function haptic(kind: HapticKind) {
    try {
      if (window.ReactNativeWebView) return window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'haptic', kind }));
      if (navigator.vibrate) navigator.vibrate(kind === 'error' ? [30, 40, 30] : kind === 'success' ? 18 : 8);
    } catch (e) {}
  }
  
  // ---------------------------------------------------------------- progress on this device
  const META_KEY = 'sb_solo', RUN_KEY = 'sb_solo_run', DAILY_KEY = 'sb_solo_daily', DUEL_KEY = 'sb_solo_duel';
  const keyFor = (mode: RunMode) => (mode === 'daily' ? DAILY_KEY : mode === 'duel' ? DUEL_KEY : RUN_KEY);
  const blankMeta = (): Meta => ({ v: 1, fans: 0, runs: 0, wins: 0, best: 0, bestNight: 0, bestSong: null, songs: 0, exacts: 0, stake: {}, crateWins: {}, shelf: {}, recent: [], daily: { streak: 0, last: null, best: 0, days: {} }, seen: {}, newUnlocks: [] });
  let meta: Meta = (() => { try { return Object.assign(blankMeta(), (JSON.parse(store.get(META_KEY) || 'null') as Partial<Meta> | null) || {}); } catch (e) { return blankMeta(); } })();
  let lookBase = progressOf(meta); // what the wardrobe had when this page opened
  const saveMeta = () => { store.set(META_KEY, JSON.stringify(meta)); queueSync(); };
  const level = () => C.levelOf(meta.fans);
  const loadSaved = <R>(k: string): R | null => { try { return JSON.parse(store.get(k) || 'null') as R | null; } catch (e) { return null; } };
  const saveRun = () => { if (!run) return; store.set(keyFor(run.mode), JSON.stringify({ run, deck: deck.save() })); };
  const dropRun = (mode: RunMode) => { try { localStorage.removeItem(keyFor(mode)); } catch (e) {} };
  
  // ---------------------------------------------------------------- who you are on the boards
  // Signed in with Google (when the cloud has a Google client id), your Daily Gig counts on the ranked board and is one
  // go a day on any device. Otherwise this device is you. Google's button doesn't work inside the app's web view.
  let gcfg: ClientConfig | null = null, rbP: Promise<Rebase> | null = null, acct: Account | null = null;
  // (a page Chrome loaded ahead doesn't sign in till it's opened: two pages renewing the session at once could lose it)
  const rbClient = () => (rbP = rbP || whenShown.then(() => import('/vendor/rebase-client.js')).then((m) => ACCOUNT.client(m)));
  async function account(): Promise<Account | null> {
    if (!cloud || !ACCOUNT.maybe()) { forget(); return (acct = null); }
    let rb: Rebase | null = null;
    try {
      rb = await rbClient(); await rb.auth.isInitialized();
      const s = rb.auth.getSession();
      // a Google account, not the guest the party rooms sign every device in as (anon_…@anonymous.local)
      acct = s && s.user && s.user.email && !s.user.isAnonymous && !/@anonymous\.local$/i.test(s.user.email) ? s : null;
      if (acct && meta.uid !== acct.user.uid) { meta.uid = acct.user.uid; saveMeta(); }
    } catch (e) { acct = null; }
    ACCOUNT.seen(acct && acct.user.uid, rb);
    return acct;
  }
  // Signed out (here or on the home page): what came with the account doesn't stay as this device's. meta.uid says
  // whose the account-only things on this device are (the name Google gave, the server's streak, a day's go).
  function forget() {
    if (!meta.uid && !meta.nameBy) return;
    if (meta.nameBy) { delete meta.name; delete meta.nameBy; }
    delete meta.uid; saveMeta();
  }
  // in the app the app signs in (Google refuses web views), when its build can
  const appGoogle = () => inApp() && !!window.SongBuzzApp!.google;
  const googleOn = () => !!(gcfg && gcfg.googleClientId) && (!inApp() || appGoogle());
  // a call to the boards, as you: the account's token when signed in (a stale one is dropped and the call made again)
  async function soloCall<R = unknown>(path: string, body?: Json | null, ms = 8000): Promise<Reply<R> | null> {
    const go = async (t: string | null): Promise<Reply<R> | null> => {
      const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const timer = ctl && setTimeout(() => ctl.abort(), ms);
      try {
        const headers: Record<string, string> = body ? { 'Content-Type': 'application/json' } : {};
        if (t) headers.Authorization = 'Bearer ' + t;
        const r = await fetch(api(path), { method: body ? 'POST' : 'GET', headers, body: body ? JSON.stringify(body) : undefined, signal: ctl ? ctl.signal : undefined });
        return { ok: r.ok, status: r.status, j: (await r.json().catch(() => ({}))) as R };
      } catch (e) { return null; } finally { if (timer) clearTimeout(timer); }
    };
    // the token as it is now: the client renews it when it has run out (the session from the page's load would not)
    const rb = (await account()) ? await rbClient() : null;
    let res = await go(rb && await rb.auth.getAccessToken().catch(() => null));
    if (res && res.status === 401 && rb) {
      const fresh = await rb.auth.refreshSession().catch(() => null);
      if (fresh && fresh.accessToken) res = await go(fresh.accessToken);
      else {
        // A refresh can fail for a moment (the network, another tab that just refreshed): this call goes without the
        // account, and the sign-in stays. Never signOut() here: it ends the session on the server, for every tab and
        // device (it was signing people out). The client itself drops a session the server really refused.
        T('solo_refresh_failed', { still: !!rb.auth.getSession() });
        if (!rb.auth.getSession()) acct = null;
        res = await go(null);
      }
    }
    return res;
  }
  // ---------------------------------------------------------------- your progress with the account
  // Signed in with Google, your level, fans, shelf and the rest (SoloCore.SYNCED) are kept with the account too, so
  // they follow you to any device. On sign-in the two copies become one (higher numbers win, shelves add up); after
  // that, every change goes up a few seconds later. A tour in progress stays on the device it's played on.
  // sb_solo_sync says whose copy this device holds: progress made for one account never goes into another's.
  const SYNC_KEY = 'sb_solo_sync';
  const syncedPart = (m: Meta): Partial<SyncedMeta> => { const o: Json = {}; for (const k of C.SYNCED as (keyof SyncedMeta)[]) if (m[k] !== undefined) o[k] = m[k]; if (m.nameBy) delete o.name; return o; };
  const devicePart = (m: Meta) => { const o: Json = {}; for (const k of Object.keys(m) as (keyof Meta)[]) if (!C.SYNCED.includes(k)) o[k] = m[k]; if (m.nameBy) o.name = m.name; return o; };
  const canon = (x: unknown) => JSON.stringify(x, (k, v: unknown) => (v && typeof v === 'object' && !Array.isArray(v) ? Object.keys(v).sort().reduce<Json>((o, key) => { o[key] = (v as Json)[key]; return o; }, {}) : v));
  let syncT: number | null = null, syncing = false, syncAgain = false;
  function queueSync(ms = 4000) {
    if (!cloud || !acct || syncing) { if (syncing) syncAgain = true; return; }
    clearTimeout(syncT!); syncT = setTimeout(syncProgress, ms);
  }
  async function syncProgress() {
    clearTimeout(syncT!); syncT = null;
    if (!cloud || !acct) return;
    if (syncing) { syncAgain = true; return; }
    syncing = true;
    try {
      const uid = acct.user.uid, held = loadSaved<SyncHeld>(SYNC_KEY) || {};
      let r = await soloCall<SaveReply>('/solo/save', null, 8000);
      for (let tries = 0; tries < 3 && r && (r.ok || r.status === 409) && acct && acct.user.uid === uid; tries++) {
        const remote = r.j.meta && typeof r.j.meta === 'object' ? r.j.meta : null, rev = +r.j.rev || 0;
        // this device's copy is another account's: the account's own copy replaces it here (the other's stays with it)
        const mineHere = !held.by || held.by === uid;
        const merged = mineHere ? C.mergeMeta(syncedPart(meta), remote || {}) : C.mergeMeta(remote || {}, {});
        if (canon(merged) !== canon(syncedPart(meta))) {
          const before = level();
          meta = Object.assign(blankMeta(), devicePart(meta), merged);
          store.set(META_KEY, JSON.stringify(meta));
          T('solo_sync_in', { from: remote ? 'account' : 'none', lv: level(), was: before });
          redrawAfterSync();
        }
        if (remote && canon(merged) === canon(C.mergeMeta(remote, {}))) { store.set(SYNC_KEY, JSON.stringify({ by: uid, rev })); break; }
        r = await soloCall<SaveReply>('/solo/save', { meta: merged, rev }, 10000);
        if (r && r.ok) { store.set(SYNC_KEY, JSON.stringify({ by: uid, rev: +r.j.rev || rev + 1 })); break; }
        held.by = uid; // merged once already: what comes back (409) is merged with what's here now
      }
    } catch (e) {} finally {
      syncing = false;
      if (syncAgain) { syncAgain = false; queueSync(1500); }
    }
  }
  // progress came from the account: the screens that show it are drawn again (never mid-song)
  function redrawAfterSync() {
    refreshBar();
    if (ui.screen === 'menu') menu();
    else if (ui.screen === 'profile') profile();
    else if (ui.screen === 'shelf') shelf();
  }
  // a change not yet sent when the page goes away: send it now
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden' && syncT) syncProgress(); });
  // the notifications and the friends board go as Solo's own sign-in (push.ts would use the site's)
  pushCallsAs(async <T>(path: string, body: unknown = {}, method: 'POST' | 'GET' = 'POST') => { const r = await soloCall<T>(path, method === 'GET' ? null : body as Json); return r && r.ok ? r.j : null; });
  // you and your friends on a day's Daily, as a board the page draws (null: signed out, or it failed)
  const friendsBoardJson = async (day: string): Promise<{ friends: number; board: Board } | null> => {
    if (!acct) return null;
    const fb = await dailyFriendsBoard(day);
    if (!fb) return null;
    const me = fb.rows.find((r) => r.me) || null;
    return { friends: fb.friends, board: { n: fb.rows.length, top: fb.rows, me, streak: null, ghost: null, you: '' } };
  };
  const boardJson = async (kind: BoardKind, key: string) => { const r = await soloCall<Board>(kind === 'daily' ? '/solo/daily/board?day=' + key + '&sid=' + deviceId() : '/solo/duel/' + key + '?sid=' + deviceId(), null, 6000); return r && r.ok ? r.j : null; };
  if (cloud) account().then((s) => { refreshBar(); if (s) syncProgress(); });
  if (cloud) fetch(api('/client-config')).then((r) => (r.ok ? r.json() : null)).then((c) => { gcfg = c; refreshBar(); view.google(); }).catch(() => {});
  // the chip in the bar: "Sign in" (only where Google sign-in works), or you
  const firstName = () => meta.name || (acct && String(acct.user.displayName || acct.user.name || '').trim().split(/\s+/)[0]) || '';
  // something here worth keeping: the sign-in says so
  const hasProgress = () => meta.fans > 0 || meta.runs > 0 || Object.keys(meta.shelf).length > 0;
  // what signing in does, and what stays on this device
  function accountSheet() { openTip('account', { keep: hasProgress() }); }
  // the app answers a sign-in with an ID token, or cancelled, or an error
  window.__soloGoogle = (r) => {
    view.signing(false);
    if (r && r.idToken) return onGoogle({ credential: r.idToken });
    if (r && r.error) toast(t('Couldn’t sign in with Google. Try again.'));
  };
  function appSignIn(b?: HTMLButtonElement) {
    if (!googleOn()) return;
    view.signing(true, b);
    toApp({ type: 'google-signin', webClientId: gcfg!.googleClientId! });
  }
  async function onGoogle(resp: GoogleCredential) {
    let s: Account | null = null;
    try { const rb = await rbClient(); await rb.auth.isInitialized(); await rb.auth.signInWithGoogle({ idToken: resp.credential }); s = await account(); } catch (e) { console.error(e); }
    if (!s) return toast(t('Couldn’t sign in with Google. Try again.'));
    T('solo_google', {});
    T('signup_done', { where: ui.screen === 'over' && run ? (run.mode === 'daily' ? 'daily_end' : 'tour_end') : ui.screen || 'solo' });
    // today's go from this device (if any) becomes the account's
    await soloCall('/solo/daily/claim', { day: today(), sid: deviceId() });
    const u = s.user, first = String(u.displayName || u.name || '').trim().split(/\s+/)[0];
    // the name Google gives, unless you chose one on this device (another account's Google name goes)
    if (first && (!meta.name || (meta.nameBy && meta.nameBy !== u.uid))) { meta.name = first.slice(0, 16); meta.nameBy = u.uid; saveMeta(); }
    toast(t('Signed in as {name}', { name: firstName() || u.email }));
    syncProgress();
    closeTip();
    view.signedIn();
    refreshBar();
    if (ui.screen === 'dailygate') return startDaily();
    if (ui.screen === 'board') return dailyBoard();
    if (ui.screen === 'profile') return profile();
    refreshBoards();
  }
  async function signOutGoogle() {
    ACCOUNT.out();
    try { const rb = await rbClient(); await rb.auth.signOut(); } catch (e) {}
    if (inApp()) toApp({ type: 'google-signout' });
    acct = null; forget();
    closeTip();
    refreshBar();
    if (ui.screen === 'board') dailyBoard();
    else if (ui.screen === 'profile') profile();
  }
  
  // the day, for the Daily Gig (today, dayNo, untilTomorrow…) is in daily.ts, shared with the home page's Daily card
  // The daily's standing among everyone who played it (the cloud keeps the scores; elsewhere there are none)
  // The score goes up after every gig (an unfinished go keeps what it had) and once more at the end (final).
  // It counts only with today's ticket from /solo/daily/start.
  async function postDaily(r: Run, final = true) {
    if (!r.token) return null;
    const cum = cumOf(r);
    const res = await soloCall<DailyScore>('/solo/daily', { day: r.day, sid: deviceId(), token: r.token, final, name: meta.name || null, hype: r.totalHype, songs: cum.length, rights: r.rights, won: r.won, cum });
    if (!res || !res.ok) return null;
    keepStreak(res.j.board);
    const d = meta.daily.days[r.day!]; if (d && final) { d.standing = res.j; saveMeta(); }
    return res.j;
  }
  // today's go starts on the server: a ticket, or "already played" (on this device or, signed in, on any)
  async function dailyTicket(day: string) {
    const res = await soloCall<DailyTicket>('/solo/daily/start', { day, sid: deviceId() });
    return res && res.ok ? res.j : null;
  }
  async function refreshStanding(day: string) {
    const d = meta.daily.days[day]; if (!d) return null;
    const r = await soloCall<DailyStanding>('/solo/daily?day=' + day + '&hype=' + d.hype + '&sid=' + deviceId(), null, 6000);
    if (!r || !r.ok) return null;
    d.standing = r.j; saveMeta(); return r.j;
  }
  function standingText(s: DailyStanding | null | undefined) {
    if (!s || !s.n) return '';
    if (s.n <= 1) return t('You’re the first to play today!');
    const p = Math.round((100 * s.below!) / (s.n - 1));
    return p >= 100 ? t('Best score of {n} players today!', { n: fmt(s.n) }) : t('You beat {p}% of {n} players today', { p, n: fmt(s.n) });
  }
  // In the app: a reminder for tomorrow's daily (the app schedules it; it answers through window.__soloReminder)
  const inApp = () => !!(window.SongBuzzApp && window.ReactNativeWebView);
  const toApp = (m: AppMessage) => { try { window.ReactNativeWebView!.postMessage(JSON.stringify(m)); } catch (e) {} };
  // what tomorrow's reminder says: its number and its Legend, in the page's language
  const tomorrowsGig = (ahead: Record<string, LegendWords>) => ({ n: dayNo(tomorrow()), legend: ahead[tomorrow()] || null });
  window.__soloReminder = (r) => {
    meta.remind = r === 'on'; saveMeta();
    view.reminder(r);
  };
  // a streak survives until a day is missed. The server counts one too (an account's follows it to any device), and
  // the longer of the two is yours.
  // a missed day doesn't end a streak while you hold a Backstage Pass (it's spent when you play again)
  const passHolds = () => passHoldsOf(meta.daily);
  const dailyStreak = () => streakOf(meta.daily, meta.uid);
  // kept from an account that isn't the one here now (signed out, or someone else signed in on this device)
  // a day's go: one played as a guest is this device's; one played signed in is that account's
  const dayGo = (day: string) => dayGoOf(meta.daily, day, meta.uid);
  const mine = (x: { by?: string | null } | null | undefined) => mineOf(x, meta.uid);
  function keepStreak(b: Board | null | undefined) {
    if (!b || !b.streak || !b.streak.n) return;
    const D = meta.daily;
    const by = meta.uid || null;
    if (!D.srv || !mine(D.srv) || D.srv.by === undefined || D.srv.last < b.streak.last || (D.srv.last === b.streak.last && (D.srv.n !== b.streak.n || D.srv.pass !== (b.streak.pass || 0)))) { D.srv = { n: b.streak.n, last: b.streak.last, pass: b.streak.pass || 0, by }; saveMeta(); }
  }
  
  // ---------------------------------------------------------------- challenges: goals across runs, each worth fans
  const shelfSize = () => Object.keys(meta.shelf).length;
  const CHALLENGES: Challenge[] = [
    { id: 'firstgig', icon: 'guitar', name: 'First gig', desc: 'Clear a gig.', fans: 10, test: (r) => r && r.gigsCleared >= 1 },
    { id: 'legend1', icon: 'sword', name: 'Legend slayer', desc: 'Beat a Legend.', fans: 20, test: (r) => r && r.legendsBeaten >= 1 },
    { id: 'night3', icon: 'speaker-hifi', name: 'Arena rock', desc: 'Reach the Arena (Night 4).', fans: 30, test: (r) => r && r.mode === 'tour' && r.night >= 3 },
    { id: 'win', icon: 'crown', name: 'Headliner', desc: 'Win a tour.', fans: 100, test: (r) => r && r.won && r.mode === 'tour' },
    { id: 'streak8', icon: 'fire', name: 'On fire', desc: 'Name 8 songs in a row.', fans: 30, test: (r) => r && r.maxStreak! >= 8 },
    { id: 'streak15', icon: 'shooting-star', name: 'Unstoppable', desc: 'Name 15 songs in a row.', fans: 60, test: (r) => r && r.maxStreak! >= 15 },
    { id: 'perfect', icon: 'seal-check', name: 'Flawless', desc: 'Clear a gig with no miss and no skip.', fans: 15, test: (r) => r && r.perfectGigs! >= 1 },
    { id: 'quick', icon: 'lightning', name: 'Quick ears', desc: 'Buzz and name a song in under 2 seconds.', fans: 15, test: (r) => r && r.fastest != null && r.fastest < 2 },
    { id: 'years3', icon: 'calendar-check', name: 'Time lord', desc: 'Guess 3 years spot on in one tour.', fans: 30, test: (r) => r && r.exacts >= 3 },
    { id: 'song1k', icon: 'star-four', name: 'Big hit', desc: 'Score 1,000 hype on one song.', fans: 20, test: (r) => r && r.bestSong && r.bestSong.hype >= 1000 },
    { id: 'song10k', icon: 'rocket', name: 'Smash hit', desc: 'Score 10,000 hype on one song.', fans: 50, test: (r) => r && r.bestSong && r.bestSong.hype >= 10000 },
    { id: 'song100k', icon: 'waveform', name: 'Earthquake', desc: 'Score 100,000 hype on one song.', fans: 120, test: (r) => r && r.bestSong && r.bestSong.hype >= 100000 },
    { id: 'decades6', icon: 'clock-counter-clockwise', name: 'Time traveller', desc: 'Name songs from 6 different decades in one tour.', fans: 25, test: (r) => r && (r.decadesNamed || []).length >= 6 },
    { id: 'fullcase', icon: 'suitcase', name: 'Full case', desc: 'Have 5 stickers at once.', fans: 15, test: (r) => r && r.stickers.length >= 5 },
    { id: 'rich', icon: 'money-wavy', name: 'Money talks', desc: 'Hold $30.', fans: 20, test: (r) => r && r.cash >= 30 },
    { id: 'lucky', icon: 'clover', name: 'Lucky strike', desc: 'Make Lucky 7 pay out.', fans: 25, test: (r) => r && r.fired && r.fired.lucky7 },
    { id: 'rewind', icon: 'rewind', name: 'Backmasking', desc: 'Beat DJ Rewind.', fans: 25, test: (r) => r && (r.beaten || []).includes('rewind') },
    { id: 'wall', icon: 'wall', name: 'Through the wall', desc: 'Beat Thin Walls.', fans: 25, test: (r) => r && (r.beaten || []).includes('wall') },
    { id: 'blink', icon: 'eye', name: 'Eagle ears', desc: 'Beat Blink.', fans: 25, test: (r) => r && (r.beaten || []).includes('blink') },
    { id: 'shelf100', icon: 'books', name: 'Collector', desc: 'Have 100 songs on your shelf.', fans: 40, test: () => shelfSize() >= 100 },
    { id: 'shelf500', icon: 'bank', name: 'Archivist', desc: 'Have 500 songs on your shelf.', fans: 120, test: () => shelfSize() >= 500 },
    { id: 'daily3', icon: 'calendar-dots', name: 'Regular', desc: 'Play the Daily Gig 3 days in a row.', fans: 30, test: () => dailyStreak() >= 3 },
    { id: 'daily7', icon: 'calendar-star', name: 'Devoted', desc: 'Play the Daily Gig 7 days in a row.', fans: 80, test: () => dailyStreak() >= 7 },
    { id: 'endless', icon: 'infinity', name: 'Encore after encore', desc: 'Clear a gig in Endless.', fans: 50, test: (r) => r && r.endless && r.gigsCleared >= 16 },
  ];
  // check them all; new ones pay their fans and say so
  function checkChallenges() {
    meta.done = meta.done || {};
    const got: Challenge[] = [];
    for (const c of CHALLENGES) {
      if (meta.done[c.id]) continue;
      let ok = false; try { ok = !!c.test(run, meta); } catch (e) {}
      if (!ok) continue;
      meta.done[c.id] = today();
      meta.fans += c.fans;
      got.push(c);
    }
    if (got.length) {
      saveMeta();
      // a song's challenges stay with its screen: one landing as the gig ends would sit on the verdict or the cash
      const from = ui.screen;
      got.forEach((c, i) => setTimeout(() => { if (view.verdictOpen() || (ui.screen === 'cleared' && from !== 'cleared')) return; toast(`${t(c.name)} · +${c.fans} ${t('fans')}`, 2600, c.icon); Sound.sfx('exact'); }, 400 + i * 2700));
      T('solo_challenge', { ids: got.map((c) => c.id) });
    }
    return got;
  }
  
  // ---------------------------------------------------------------- sound
  // ?mute: no sound at all (automated tests on a computer with speakers)
  const MUTE = new URLSearchParams(location.search).has('mute');
  const Sound = (() => {
    let ac: AudioContext | null = null, master: GainNode | null = null, musicGain: GainNode | null = null;
    const cache = new Map<SongId, Promise<AudioBuffer>>(), revs = new Map<SongId, AudioBuffer>();
    let cur: Playing | null = null; // {src, gain, extra: [], id}
    function ctx() {
      if (!ac) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return null;
        ac = new AC();
        master = ac.createGain(); master.connect(ac.destination);
        if (MUTE) master.gain.value = 0;
        musicGain = ac.createGain(); musicGain.connect(master);
      }
      return ac;
    }
    function unlock() {
      const c = ctx(); if (!c) return;
      if (c.state !== 'running') c.resume().catch(() => {});
      try { const b = c.createBuffer(1, 1, 22050), s = c.createBufferSource(); s.buffer = b; s.connect(master!); s.start(0); } catch (e) {}
    }
    function load(id: SongId) {
      if (cache.has(id)) { const p = cache.get(id)!; cache.delete(id); cache.set(id, p); return p; }
      const c = ctx();
      const p = fetch(api('/audio/' + id)).then((r) => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.arrayBuffer(); })
        .then((ab) => new Promise<AudioBuffer>((res, rej) => c!.decodeAudioData(ab, res, rej)));
      p.catch(() => cache.delete(id));
      cache.set(id, p);
      while (cache.size > 10) cache.delete(cache.keys().next().value!);
      return p;
    }
    function reversed(id: SongId, b: AudioBuffer) {
      if (revs.has(id)) return revs.get(id)!;
      const o = ac!.createBuffer(b.numberOfChannels, b.length, b.sampleRate);
      for (let ch = 0; ch < b.numberOfChannels; ch++) { const s = b.getChannelData(ch), d = o.getChannelData(ch); for (let i = 0, n = s.length; i < n; i++) d[i] = s[n - 1 - i]; }
      revs.set(id, o); if (revs.size > 3) revs.delete(revs.keys().next().value!);
      return o;
    }
    let noiseBuf: AudioBuffer | null = null;
    function noise() {
      if (!noiseBuf) { noiseBuf = ac!.createBuffer(1, ac!.sampleRate * 2, ac!.sampleRate); const d = noiseBuf.getChannelData(0); for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1; }
      return noiseBuf;
    }
    function stop(fade = 0.25) {
      if (!cur || !ac) return;
      const c = cur; cur = null;
      const t = ac.currentTime;
      try { c.gain.gain.cancelScheduledValues(t); c.gain.gain.setValueAtTime(c.gain.gain.value, t); c.gain.gain.linearRampToValueAtTime(0, t + fade); } catch (e) {}
      setTimeout(() => { for (const n of [c.src, ...c.extra]) { try { n.stop(); } catch (e) {} try { n.disconnect(); } catch (e) {} } }, fade * 1000 + 60);
    }
    // Play a song with a Legend's twist: {reverse, rate, lowpass, static, blink}. Resolves when the music starts.
    async function play(id: SongId, fx: PlayFx = {}) {
      const c = ctx(); if (!c) throw new Error('no audio');
      if (c.state !== 'running' && !held) await c.resume().catch(() => {});
      const raw = await load(id);
      stop(0.15);
      const buf = fx.reverse ? reversed(id, raw) : raw;
      const src = c.createBufferSource(); src.buffer = buf; src.playbackRate.value = fx.rate || 1;
      const gain = c.createGain(); gain.gain.value = 0;
      let node: AudioNode = src;
      if (fx.lowpass) { const f = c.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = fx.lowpass; f.Q.value = 0.9; node.connect(f); node = f; }
      node.connect(gain); gain.connect(musicGain!);
      const extra: AudioBufferSourceNode[] = [];
      const t = c.currentTime;
      if (fx.static) {
        // radio static that swells and fades, over a slightly band-limited song
        const ns = c.createBufferSource(); ns.buffer = noise(); ns.loop = true;
        const bp = c.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = 2400; bp.Q.value = 0.6;
        const ng = c.createGain(); ng.gain.value = 0;
        ns.connect(bp); bp.connect(ng); ng.connect(gain);
        for (let k = 0; k < 30; k++) ng.gain.setValueAtTime(fx.static * (0.35 + 0.65 * Math.abs(Math.sin(k * 1.7 + 0.4))) * 1.4, t + k);
        ns.start(t); extra.push(ns);
      }
      let from = fx.reverse ? Math.max(0, buf.duration - 28) : fx.from != null ? Math.max(0, Math.min(fx.from, buf.duration - (fx.stopAt != null ? 1 : 16))) : 0;
      if (fx.loop) {
        // Broken Record: one slice from a third of the way in, round and round
        from = Math.max(0, Math.min(buf.duration * 0.35, buf.duration - fx.loop - 0.5));
        src.loop = true; src.loopStart = from; src.loopEnd = from + fx.loop;
      }
      if (fx.fadein) {
        // Fade In: from a faint, muffled whisper to the full song
        const f = c.createBiquadFilter(); f.type = 'lowpass'; f.frequency.setValueAtTime(250, t); f.frequency.exponentialRampToValueAtTime(12000, t + fx.fadein);
        node.disconnect(); node.connect(f); f.connect(gain);
        gain.gain.setValueAtTime(0.04, t); gain.gain.exponentialRampToValueAtTime(1, t + fx.fadein);
      } else { gain.gain.setValueAtTime(0, t); gain.gain.linearRampToValueAtTime(1, t + 0.06); }
      src.start(t, from);
      if (fx.stopAt != null && fx.stopAt > from) { const end = t + (fx.stopAt - from) / (fx.rate || 1); gain.gain.setValueAtTime(1, end - 0.08); gain.gain.linearRampToValueAtTime(0, end); } // the music stops dead at a lyric's cut
      if (fx.onEnd) src.onended = () => { if (cur && cur.src === src) { cur = null; fx.onEnd!(); } };
      if (fx.blink) { gain.gain.setValueAtTime(1, t + fx.blink - 0.15); gain.gain.linearRampToValueAtTime(0, t + fx.blink); }
      cur = { src, gain, extra, id };
      return true;
    }
    // the hit at the reveal: a Legend's twisted song plays straight once you've answered
    async function straighten(id: SongId) {
      if (!cur || cur.id !== id || !ac) return;
      const raw = await load(id).catch(() => null); if (!raw) return;
      const pos = 8;
      stop(0.2);
      const src = ac.createBufferSource(); src.buffer = raw;
      const gain = ac.createGain(); gain.gain.value = 0; src.connect(gain); gain.connect(musicGain!);
      const t = ac.currentTime + 0.05; gain.gain.setValueAtTime(0, t); gain.gain.linearRampToValueAtTime(1, t + 0.3);
      src.start(t, pos);
      cur = { src, gain, extra: [], id };
    }
    function duck(v: number) { if (musicGain && ac) { musicGain.gain.cancelScheduledValues(ac.currentTime); musicGain.gain.linearRampToValueAtTime(v, ac.currentTime + 0.2); } }
    // ---------------------------------------------------------------- effects: the band (sfx.ts)
    let fx: ReturnType<typeof soloEffects> | null = null;
    function sfx(name: SfxName, i = 0) {
      if (!ac || ac.state !== 'running') return;
      if (!fx) fx = soloEffects(ac, master!);
      fx(name, i);
    }
    // Pause: the whole sound stands still (the song, its snippet and lyric cuts) and carries on from there
    let held = false;
    function hold(on: boolean) { held = !!on; if (ac) (on ? ac.suspend() : ac.resume()).catch(() => {}); }
    return { ctx, unlock, load, play, stop, straighten, duck, sfx, hold, held: () => held, time: () => (ac ? ac.currentTime : 0) };
  })();
  
  // ---------------------------------------------------------------- songs: the crate for this run
  // deck: the songs still to come (dealt in order), all: every song of the run (for wrong answers)
  const deck = (() => {
    let queue: DeckSong[] = [], all: DeckSong[] = [], info = {};
    const sims = new Map<SongId, Promise<Similar | null>>();
    return {
      set(list: DeckSong[]) { queue = list.slice(); all = list.slice(); },
      save: () => ({ queue: queue.slice(0, 200), all: all.slice(0, 400) }),
      restore(d: DeckSave | null | undefined) { queue = (d && d.queue) || []; all = (d && d.all) || queue.slice(); },
      left: () => queue.length,
      all: () => all,
      // the songs a gig deals (C.dealPlan: the best known first, then harder gig by gig)
      take: (n: number, plan: DealPlan, rnd?: () => number) => C.dealFrom(queue, n, plan, rnd),
      // a song that couldn't play goes away for good
      peek: (n: number) => queue.slice(0, n),
      similar(song: Song) {
        if (!song || !song.id) return Promise.resolve(null);
        if (!sims.has(song.id)) {
          const p = getJson<Similar>(api('/similar/' + song.id + (song.artistId ? '?a=' + song.artistId : '')), 6000).catch(() => null);
          sims.set(song.id, p);
          if (sims.size > 60) sims.delete(sims.keys().next().value!);
        }
        return sims.get(song.id);
      },
    };
  })();
  const lc = (s: string | null | undefined) => String(s || '').toLowerCase();
  const baseTitle = (t: string | null | undefined) => lc(t).replace(/\s*[([].*?[)\]]/g, '').replace(/\s+-\s+.*$/, '').replace(/[^a-z0-9À-ɏ]+/g, ' ').trim();
  function shuffleWith<T>(rnd: () => number, a: T[]) { a = a.slice(); for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; }
  // a tiny seeded generator, for the daily's answers (the same for everyone)
  function seeded(seed: string) { let s = C.hashStr(seed) || 1; return () => { s = (s + 0x6d2b79f5) >>> 0; let t = Math.imul(s ^ (s >>> 15), s | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; }
  
  // Fetch the songs for a crate from the catalogue (cloud), else from Deezer playlists by decade (LAN, or the
  // catalogue being down). Songs from the last few runs go to the back.
  const DECADE_PL: Record<string, string> = { '70s': '@pl:1470022445', '80s': '@pl:867825522', '90s': '@pl:878989033', '2000s': '@pl:248297032', '2010s': '@pl:715215865', Rock: '@pl:1306931615', Dance: '@pl:6237312204', 'Hip-hop': '@pl:1996494362', Latin: '@pl:178699142' };
  // seed: a Daily's or a challenge's, so everyone gets the same songs in the same order (none sent back for being recent)
  async function crateSongs(crateId: string, country: string | null, seed?: string) {
    const crate = C.CRATES[crateId] || C.CRATES.mixtape;
    const tags = crate.tags.map((x) => x.replace('{cc}', country || ''));
    const per = Math.min(300, Math.ceil(260 / tags.length) + 10);
    // songs known where you are, at every difficulty (spread), from the files that ship with the page (common.ts)
    let lists: (DeckSong[] | null)[] = await Promise.all(tags.map((tag) => catalogueSongs(tag, country, per, true, seed ? seeded(seed + ':' + tag) : undefined).then((j) => j.tracks || []).catch(() => null)));
    let songs = (lists.filter(Boolean) as DeckSong[][]).flat();
    if (songs.length < 40) {
      // no catalogue here: playlists, dated when dealt
      const qs = tags.map((tag) => (tag.startsWith('local:') && SongBuzzEngine.LOCAL[country!] ? '@pl:' + SongBuzzEngine.LOCAL[country!].all.slice(0, 4).join(',') : DECADE_PL[tag])).filter(Boolean);
      if (!qs.length) qs.push(...Object.values(DECADE_PL).slice(0, 5));
      lists = await Promise.all(qs.map((q, i) => getJson<Pool>(api('/pool?q=' + encodeURIComponent(q))).then((j) => (j.tracks || []).slice(0, 80).map((t) => ({ ...t, hint: tags[i] }))).catch(() => [])));
      songs = (lists as DeckSong[][]).flat();
    }
    const seen = new Set<string>(), out: DeckSong[] = [];
    for (const s of songs) { const k = lc(s.title) + '|' + lc(s.artist); if (!s.id || seen.has(k)) continue; seen.add(k); out.push(s); }
    if (out.length < 20) throw new Error('no songs');
    // how hard each song is (0..100): the catalogue's difficulty (how known it is, and how often players name it); for
    // a song without one but with a fame, its place in this crate by fame
    const known = out.filter((s) => s.difficulty == null && s.fame != null).sort((a, b) => b.fame! - a.fame!);
    known.forEach((s, i) => { s.diff = Math.round((100 * i) / Math.max(1, known.length - 1)); });
    out.forEach((s) => { if (s.difficulty != null) s.diff = s.difficulty; });
    if (seed) return shuffleWith(seeded(seed + ':deck'), out);
    const recent = new Set(meta.recent);
    const fresh = shuffleWith(Math.random, out.filter((s) => !recent.has(s.id))), old = shuffleWith(Math.random, out.filter((s) => recent.has(s.id)));
    return fresh.concat(old);
  }
  // An old daily or challenge (before RULES.tourFrom): Deezer's decade playlists (the same lists for everyone),
  // ordered by a hash of the day. From then on they deal a tour's songs: crateSongs with the run's seed.
  async function dailySongs(day: string) {
    const qs = ['70s', '80s', '90s', '2000s', '2010s'].map((d) => DECADE_PL[d]);
    const lists = await Promise.all(qs.map((q) => getJson<Pool>(api('/pool?q=' + encodeURIComponent(q))).then((j) => j.tracks || []).catch(() => [])));
    const seen = new Set<string>(), out: DeckSong[] = [];
    for (const s of lists.flat()) { const k = lc(s.title) + '|' + lc(s.artist); if (!s.id || seen.has(k)) continue; seen.add(k); out.push(s); }
    if (out.length < 30) throw new Error('no songs');
    out.sort((a, b) => C.hashStr(day + ':' + a.id) - C.hashStr(day + ':' + b.id));
    return out.slice(0, 70);
  }
  // Release years (and genres) for songs that came without them, a few at a time.
  const yearAsks = new Map<SongId, Promise<void>>();
  function fillYear(s: DeckSong) {
    if (s.year || s.noYear) return Promise.resolve();
    if (!yearAsks.has(s.id)) yearAsks.set(s.id, getJson<SongInfo>(api('/year/' + s.id + '?t=' + encodeURIComponent(s.title || '') + '&a=' + encodeURIComponent(s.artist || '')), 7000).then((j) => { if (j.year) s.year = j.year; else s.noYear = true; if (j.genre && !s.genre) s.genre = j.genre; }).catch(() => { s.noYear = true; }));
    return yearAsks.get(s.id);
  }
  async function fillYears(songs: DeckSong[], max = 6) {
    const todo = songs.filter((s) => !s.year && !s.noYear);
    for (let i = 0; i < todo.length; i += max) await Promise.all(todo.slice(i, i + max).map(fillYear));
  }
  
  // the four answers for a song: the closer the run gets to the Stadium, the closer the wrong ones
  async function optionsFor(cur: CurrentSong) {
    const song = cur.song, rules = C.gigRules(run!);
    // 0: other decades · 1: the same decade · 2: related artists · 3: one by the same artist · 4: two by the same artist
    const base = C.oneNight(run!) ? (run!.gigNo >= 2 ? 2 : 1) : Math.min(4, run!.night);
    // Who sings it?: four different artists, so never two songs by the same one
    const byArtist = rules.ask === 'artist', hardness = byArtist ? Math.min(2, base) : rules.hard ? Math.max(3, base) : base;
    const sim = { ...(await Promise.race([deck.similar(song), wait(hardness >= 2 || rules.sameArtist ? 2500 : 1200).then(() => null)])) };
    const used = new Set([baseTitle(song.title)]), artists = new Set([lc(song.artist)]);
    const others = deck.all().filter((x) => x.id !== song.id && lc(x.artist) !== lc(song.artist));
    const rnd = C.seeded(run!) ? seeded(run!.seed + ':' + song.id) : Math.random;
    const out: AnswerOption[] = [];
    const take = (list: AnswerOption[] | null | undefined, max: number, sameArtistOk?: boolean) => {
      let k = 0;
      for (const x of shuffleWith(rnd, list || [])) {
        if (out.length >= 3 || k >= max) break;
        if (!x || !x.title || !x.artist) continue;
        const bt = baseTitle(x.title), a = lc(x.artist);
        if (!bt || used.has(bt) || (!sameArtistOk && artists.has(a))) continue;
        used.add(bt); artists.add(a); out.push({ title: x.title, artist: x.artist }); k++;
      }
    };
    const dec = cur.decade;
    // even the easy ones make sense next to it: the same language and kind of music, the closest first (never the
    // Bee Gees against a reggaeton hit); easier is only further in years
    const near = (list: DeckSong[], n = 8) => SongBuzzEngine.nearest(song as SongLike, list as SongLike[], n);
    // a Daily or a challenge deals everyone the same answers: nothing from the server's lookups (Deezer's related
    // artists and top songs, the catalogue's peers can differ by player and hour), only the seeded deck, nearest first
    if (C.seeded(run!)) { sim.peers = null; sim.related = null; sim.same = null; }
    if (rules.sameArtist && !byArtist) { take((sim.same || []).map((x) => ({ ...x, artist: song.artist })), 3, true); take(sim.related, 3); }
    else if (hardness === 0) { take(near(others.filter((x) => C.decadeOf(x.year) !== dec)), 3); }
    else if (hardness === 1) { take(near(others.filter((x) => C.decadeOf(x.year) === dec), 6), 1); take(near(others), 3); }
    else if (hardness === 2) { take(sim.related, 1); take(sim.peers, 1); take(near(others.filter((x) => C.decadeOf(x.year) === dec), 6), 1); take(near(others), 3); }
    else if (hardness === 3) { take((sim.same || []).map((x) => ({ ...x, artist: song.artist })), 1, true); take(sim.related, 1); take(sim.peers, 1); take(near(others), 3); }
    else { take((sim.same || []).map((x) => ({ ...x, artist: song.artist })), 2, true); take(sim.related, 2); take(sim.peers, 2); take(near(others), 3); }
    take(sim.peers, 3); take(near(others, 16), 3); take(others, 3);
    const opts = shuffleWith(rnd, [{ title: song.title, artist: song.artist, right: true }, ...out.slice(0, 3)]);
    return byArtist ? opts.map((o) => ({ title: o.artist, right: o.right })) : opts;
  }
  
  // The Timeline set's answers: the spots around the right one on the line ("1982 – 1997", between which songs)
  function slotOpts(cur: CurrentSong): AnswerOption[] {
    const rnd = C.seeded(run!) ? seeded(run!.seed + ':slot:' + cur.song.id) : Math.random;
    return C.slotOptions(run!, rnd).map((o) => ({
      title: o.lo && o.hi ? o.lo.year + ' – ' + o.hi.year : o.hi ? t('Before {y}', { y: o.hi.year }) : t('After {y}', { y: o.lo!.year }),
      artist: o.lo && o.hi ? t('after {a}, before {b}', { a: o.lo.title, b: o.hi.title }) : o.hi ? t('before {s}', { s: o.hi.title }) : t('after {s}', { s: o.lo!.title }),
      right: o.right,
    }));
  }

  // ---------------------------------------------------------------- the state of the page
  let run: Run | null = null;          // the run being played (solo-core), saved after every step
  let ui: Ui = { screen: 'menu' };
  let tipFor: TipFor | null = null;       // the sticker or tape being read
  // the decades this player names well, past the first few songs: 0..1 above an even chance (the first two nights lean on them)
  const knownDecades = () => {
    const out: Record<string, number> = {};
    for (const [d, [r, n]] of Object.entries(meta.dec || {})) if (n >= 3) out[d] = Math.max(0, (r / n - 0.5) * 2);
    return out;
  };
  const supply = (n: number) => {
    // a Daily or a challenge draws from its deck the same way for everyone at the same point of the tour (and leans on
    // no one's own decades)
    const g = run && run.gig, same = !!run && C.seeded(run), rnd = same && g ? seeded(run!.seed + ':deal:' + run!.night + ':' + run!.gigNo + ':' + g.played + ':' + g.hand.length) : undefined;
    const out = deck.take(n, { ...C.dealPlan(run), prefer: run && !same && run.night < 2 ? knownDecades() : null }, rnd);
    // keep a few songs dated ahead of the dealer
    fillYears(deck.peek(6)).catch(() => {});
    out.forEach((s) => deck.similar(s));
    return out;
  };
  
  const DECADE_COLOR: Record<string, string> = { '50s': '#2ee6a6', '60s': '#2ee6a6', '70s': '#ff6a3d', '80s': '#ff5f9e', '90s': '#c6f432', '2000s': '#3fb6ff', '2010s': '#2a4bff', '2020s': '#ffc531' };
  const FAMILY_ICON: Record<string, string> = { Pop: 'microphone-stage', Rock: 'guitar', Dance: 'disco-ball', 'Hip-hop': 'baseball-cap', 'R&B': 'music-notes-simple', Latin: 'person-arms-spread' };
  const decLabel = (d: string | null | undefined) => (d ? decadeText(d) : '');
  // decades read as decades ("años 80"), everything else through the dictionary
  const tv = (x: string) => (/^\d0s$|^\d{4}s$/.test(x) ? decadeText(x) : t(x));
  const stickerTxt = (s: Sticker) => ({ name: C.stickerName(s, tv) + (s.foil ? ' ✦' : ''), desc: C.stickerDesc(s, tv) + (s.foil ? ' · ' + t('Foil: ×1.5 its effect') : '') });
  // what a sticker does, in two or three words, for its chip on the case ("Rock +60", "+4 combo")
  function shortLabel(s: Sticker) {
    const d = C.STICKERS[s.id]; if (!d || !d.short) return stickerTxt(s).name;
    return t(d.short).replace('{v}', s.v ? (d.variant === 'decade' ? s.v : t(s.v)) : '').replace('{n}', String(s.n || 0)).replace('{x}', fmtMult(1 + 0.5 * (s.n || 0))) + (s.foil ? ' ✦' : '');
  }
  // Tapes. One rule says which work right now (tapeReady), for the Tapes button in the bar, the one-line hint on the
  // screen where a tape helps, and the sheet that lists them all with what each does and when.
  function tapeReady(id: string) {
    const d = C.TAPES[id], g = run && run.gig;
    if (!d || !g) return false;
    const phase = ui.screen === 'shop' ? 'shop' : run!.phase, c = g.cur;
    if (!d.when.includes(phase)) return false;
    if (ui.screen === 'play' && (!ui.opts || ui.answered)) return false; // while the song is cued up, or once answered
    if (g.offers && phase !== 'shop') return false; // choosing the set sets the gig's songs and skips anew
    // picking a record they're armed for the next song (once each); on a song, they're for that song
    if (id === 'fifty' || id === 'crystal' || id === 'second') return phase === 'hand' ? !(g.armed && g.armed[id]) : !!c && !c[id] && !(id === 'fifty' && (c.hidden || []).length);
    if (id === 'hypeshot') return !g.hypeShot;
    if (id === 'dig') return C.SETS[g.set] ? C.SETS[g.set].skips !== 0 : true; // a set with no Skip button
    if (id === 'goldpress') return !!(g.hand && g.hand.length);
    return true;
  }
  const readyTapes = () => (run ? run.tapes.map((id, i) => ({ id, i })).filter((x) => tapeReady(x.id)) : []);
  // the tape worth a line here, the most useful first: on the play screen the ones for this song, on the records the
  // ones for the gig, on the year dare the Crystal Ball (Royalty Check only where it's the moment: picking or the shop)
  const HINT_ORDER: Record<'play' | 'hand' | 'bet', string[]> = { play: [], hand: ['fifty', 'second', 'crystal', 'fresh', 'goldpress', 'hypeshot', 'extra', 'dig', 'royalty'], bet: [] }; // tapes are for picking a record
  function tapeHintOf() {
    const where = ui.screen === 'play' ? 'play' : ui.screen === 'hand' ? 'hand' : ui.screen === 'result' && run!.phase === 'bet' ? 'bet' : null;
    if (!where) return null;
    const ready = readyTapes(), pick = HINT_ORDER[where].map((id) => ready.find((x) => x.id === id)).find(Boolean);
    if (!pick) return null;
    return { i: pick.i, d: C.TAPES[pick.id], more: ready.length - 1 };
  }
  // every tape: what it does, when it works, and Use on the ones that work now
  function tapesSheet() {
    if (!run || !run.tapes.length) return;
    tipFor = { kind: 'tapes' };
    openTip('tapes', { rows: run!.tapes.map((id, i) => ({ id, i, d: C.TAPES[id], ok: tapeReady(id) })) });
  }
  // the big number: the gig against its target on tour; the whole night's score in an old daily or challenge
  function meterOf() {
    const g = run!.gig!;
    if (C.oneNight(run!)) {
      const total = C.nightPlays(run!), done = run!.history.filter((h) => h.r !== 'skip').length, left = Math.max(0, total - done);
      return { now: run!.totalHype, of: null, pct: Math.min(100, (100 * done) / total), full: false, label: left ? (left === 1 ? t('your score · 1 song to go') : t('your score · {n} songs to go', { n: left })) : t('your score') };
    }
    const out = C.toPlayOut(run!);
    return { now: g.hype, of: g.target, pct: Math.min(100, (100 * g.hype) / g.target), full: g.hype >= g.target, label: out ? (out === 1 ? t('Done! Last song') : t('Done! {n} songs to go', { n: out })) : g.hype >= g.target ? t('Target reached!') : toGoLine(g.target - g.hype) };
  }
  // "152 to go", and what that is in songs: about how many named in a row get there (C.songsToGo), or all of them
  function toGoLine(need: number) {
    const g = run!.gig!, k = C.songsToGo(run!), left = g.plays - g.played, n = fmt(need);
    if (!k || !left) return t('{n} to go', { n });
    if (k > left) return t('{n} to go · name every song left', { n });
    return k === 1 ? t('{n} to go · one more song does it', { n }) : t('{n} to go · about {k} in a row', { n, k });
  }
  // the gig's status, for the head-up display
  function hudOf(): Hud {
    const g = run!.gig!, M = meterOf();
    const where = (C.oneNight(run!) ? nightLabel() : t('Night {n}', { n: run!.night + 1 })) + ' · ' + (g.set !== 'name' && !g.legend ? t(C.SETS[g.set].name) : gigName(run!.gigNo));
    // the song on screen: during a result it's the one just answered (already counted), otherwise the next one
    const onResult = ui.screen === 'result' || run!.phase === 'bet' || run!.phase === 'result';
    const songNo = Math.max(1, Math.min(onResult ? g.played : g.played + 1, g.plays));
    // target reached before the 4th song: the gig ends at the 4th, so that's the count ("Canción 4 de 4", not "de 5")
    const out = C.toPlayOut(run!), ofSongs = out ? g.played + out + (onResult ? 0 : 0) : g.plays;
    const badges: (Badge | null)[] = [
      // what a streak is worth: the next named song's combo (a miss sends it back to ×1). Shown from the start, so ×1 means something
      { cls: run!.streak ? 'fire' : 'calm', flame: !!run!.streak, icon: 'fire', title: t('Next song ×{n}', { n: run!.streak + 1 }), text: t('Next song ×{n}', { n: run!.streak + 1 }), short: '×' + (run!.streak + 1) },
      g.request ? { cls: 'req', icon: 'megaphone', title: t('{d} songs +{x} combo', { d: decLabel(g.request), x: C.REQUEST_COMBO }), text: decLabel(g.request).replace(/^./, (c) => c.toUpperCase()) + ' +' + C.REQUEST_COMBO } : null, // short: it shares the row with the combo
      ghostLine(),
      g.hypeShot ? { cls: 'fire', icon: 'battery-charging', text: t('+{n} combo', { n: C.BOOSTER_COMBO }) } : null,
      momentBadge(),
      g.skipBank ? { icon: 'tag', text: t('+{n} combo on the next song', { n: g.skipBank }), short: '+' + g.skipBank } : null,
      ...(Object.keys(g.armed || {}) as (keyof Armed)[]).filter((k) => g.armed![k] && C.TAPES[k]).map((k) => ({ cls: 'armed', icon: C.TAPES[k].icon, title: t('{tape} on the next song', { tape: t(C.TAPES[k].name) }), text: t('{tape} on the next song', { tape: t(C.TAPES[k].name) }), short: t(C.TAPES[k].name) })),
    ];
    return { M, where, songNo, ofSongs: Math.max(songNo, ofSongs), reached: M.full, danger: lastSong(), skipsLine: C.SETS[g.set].skips === 0 && !g.skips ? '' : g.skips === 1 ? t('1 skip left') : t('{n} skips left', { n: g.skips }), badges: badges.filter((b) => b !== null) };
  }
  // the crowd's moment, while it's on: what to do for it
  function momentBadge(): Badge | null {
    const g = run!.gig!, m = g.moment && g.moment.live ? g.moment : g.cur && !g.cur.done && g.cur.moment ? g.cur.moment : null;
    // short, when the song's line is tight: what it's worth
    const short = !m ? '' : m.id === 'decade' ? decLabel(m.v) + ' ×2' : m.id === 'lights' ? '+4' : m.id === 'chant' ? '×2' : '+' + money(C.RULES.tipCash);
    return m ? { cls: 'fire', icon: C.MOMENTS[m.id].icon, text: momentText(m, 'badge'), title: momentText(m, 'badge'), short } : null;
  }
  // a first-time note: shown once, on the first screen that needs it
  const COACH = {
    intro: () => t('<b>Your first gig.</b> Name songs to fill the score bar. Fast answers score more, and every song in a row multiplies the next one.'),
    daily: () => t('<b>The Daily Gig:</b> a tour with the same songs for everyone today, and one go. Your points are your score.'),
    pick: () => t('<b>Pick the decade you know best.</b> The other records stay in your hand.'),
    // the first time special sleeves are dealt: what they pay, and their catch
    specials: () => t('<b>Special sleeves pay more, with a catch.</b> Hot ones play fast, Gold ones stop after 5 seconds, Neon ones hide the artists, and Lyric ones stop mid-song: finish the line.'),
    play: () => t('<b>Listen first.</b> Know it? Buzz for +{x} combo, then pick it within {w} seconds. Or wait: the answers turn up on their own.', { x: C.BUZZ_COMBO, w: C.RULES.buzz.window }),
    tally: () => t('<b>Points × combo = score.</b> Faster answers give more points. Every song in a row raises your combo; a miss sends it back to 1.'),
    sets: () => t('<b>Each set plays a different way</b>, and each comes with a prize.'),
  };
  const coachLine = (key: keyof typeof COACH) => (meta.seen[key] ? '' : COACH[key]());
  function seen(key: string) { if (!meta.seen[key]) { meta.seen[key] = 1; saveMeta(); } }
  // ---------------------------------------------------------------- the bar on top of every screen
  // Outside a run: back (where there's somewhere to go back to), the logo (the Solo menu; on the menu itself, the
  // site's home), your account and, on the menu, the language. In a run: pause, the logo (pause too), your encores
  // and your cash. Every screen has it, in the same place.
  const RUN_SCREENS = new Set(['intro', 'starter', 'hand', 'play', 'result', 'cleared', 'shop']);
  const BACK: Partial<Record<ScreenName, string | (() => string)>> = { howto: () => (ui.back === 'resume' ? 'resume' : 'menu'), shelf: 'menu', profile: 'menu', crates: 'menu', board: 'menu', duel: 'menu', over: 'menu', dailygate: 'menu', error: 'menu' };
  // the name of a one-night run: today's daily, or a challenge
  const nightLabel = () => (run!.mode === 'duel' ? t('Challenge') + (run!.duel && run!.duel.by && run!.duel.by.name && !run!.duel.mine ? ' · ' + run!.duel.by.name : '') : t('Daily #{n}', { n: dayNo(run!.day!) }));
  // "Gig 2 of 3", or the Legend for the third
  const gigName = (k: number) => (k === 2 ? t('Legend') : t('Gig {n} of {m}', { n: k + 1, m: 3 }));
  function legendName(L: GigLegend) { return L.headliner ? t('The Headliner') : t(C.LEGENDS[L.ids[0]].name); }
  // today's Daily Legend (gig 3), as the Daily itself deals it, for the menu's card: a twist to talk about before playing
  const dailyLegends = new Map<string, GigLegend | null>();
  function dailyLegend(day: string) {
    if (!dailyLegends.has(day)) { try { dailyLegends.set(day, C.dailyLegend(day)); } catch (e) { dailyLegends.set(day, null); } }
    return dailyLegends.get(day);
  }
  // a Legend is a character in shades
  function legendFace(L: GigLegend) { return avatar({ pid: 'legend-' + L.ids.join(), color: legendColor(L), shades: true }, 'grin'); }
  function legendColor(L: GigLegend) { return L.headliner ? '#ff3d5a' : C.LEGENDS[L.ids[0]].color; }
  
  // ---------------------------------------------------------------- competing: names, boards, challenges, ghosts
  // Your name on the boards is kept here; your character comes from a hash the server makes of your device id.
  const LOOK_COLORS = ['#ff6b4a', '#c6f432', '#38bdf8', '#ffb020', '#2ee6a6', '#ff5fa2', '#4f8cff', '#ffe14d', '#22d3ee', '#ff8a1f', '#4ade80', '#ff3d5a'];
  function lookAvatar(look: string | null | undefined, mood: Mood = 'smile') { const n = parseInt(String(look || '0').slice(0, 6), 16) || 0; return avatar({ pid: look, color: LOOK_COLORS[n % LOOK_COLORS.length] }, mood); }
  // your score after every song of a one-night run (what a ghost races)
  function cumOf(r: Run) { let tot = 0; return r.history.filter((h) => h.r !== 'skip').map((h) => (tot += h.hype || 0)); }
  function ghostLine() {
    const G = run && run.ghost;
    if (!G || !Array.isArray(G.cum) || !G.cum.length || !C.seeded(run!)) return null;
    const name = G.name || t('the leader'), nm = shortName(name);
    const mine = cumOf(run!), k = mine.length;
    if (!k) return { cls: 'ghost', icon: 'sword', title: t('{name} scored {n}: beat it', { name, n: fmt(G.hype) }), text: t('Beat {name}: {n}', { name: nm, n: fmt(G.hype) }), short: fmt(G.hype) };
    const theirs = G.cum[Math.min(k, G.cum.length) - 1] || 0, d = mine[k - 1] - theirs;
    // the race's split, short ("▼ 500 · Marta"): the long one is its title
    return { cls: 'ghost ' + (d >= 0 ? 'ahead' : 'behind'), icon: 'sword', title: d >= 0 ? t('{d} ahead of {name}', { d: fmt(d), name }) : t('{d} behind {name}', { d: fmt(-d), name }), text: (d >= 0 ? '▲ ' : '▼ ') + fmt(Math.abs(d)) + ' · ' + nm, short: (d >= 0 ? '▲ ' : '▼ ') + fmt(Math.abs(d)) };
  }
  // a board name, short enough for a pill: the words that fit in 12 letters ("Marta", "The Night Owl" → "The Night")
  function shortName(name: string) {
    const w = name.split(/\s+/); let out = w[0];
    for (const x of w.slice(1)) { if ((out + ' ' + x).length > 12) break; out += ' ' + x; }
    return out;
  }
  // passing the ghost (or being passed) is a moment
  function rivalMoment() {
    const G = run!.ghost;
    if (!G || !Array.isArray(G.cum) || !C.seeded(run!)) return;
    const mine = cumOf(run!), k = mine.length; if (!k) return;
    const d = mine[k - 1] - (G.cum[Math.min(k, G.cum.length) - 1] || 0), sign = d > 0 ? 1 : d < 0 ? -1 : 0;
    const was = run!.ghostSign || 0;
    run!.ghostSign = sign;
    const name = G.name || t('the leader');
    if (sign > 0 && was <= 0 && k > 1) { toast(t('You overtook {name}!', { name }), 2600, 'sword'); Sound.sfx('exact'); haptic('success'); }
    else if (sign < 0 && was > 0) { toast(t('{name} is back in front', { name }), 2600, 'sword'); Sound.sfx('boo'); }
    else view.splitFlash(d >= 0, fmt(Math.abs(d)) + ' · ' + shortName(name)); // otherwise the split, like a race's: ▲ 500 · Marta, ▼ behind
  }
  // the line under a board
  function boardFoot(b: Board, max: number) {
    const unranked = !!(b && b.me && b.me.unranked);
    return unranked ? (b.signedIn ? t('Today’s go started before you signed in, so it isn’t ranked. Tomorrow’s will be.') : googleOn() ? t('You’re not ranked: sign in with Google to count.') : t('You’re not ranked: ranked play needs a Google sign-in, which this version of the app doesn’t have yet.')) : b.n > max ? t('{n} players', { n: fmt(b.n) }) : '';
  }
  // a name for the boards: asked once, right where it shows (the Daily's boards ask you to sign in instead)
  function nameAsk(daily = false) {
    if (daily && googleOn()) return acct ? '' : 'google';
    return meta.name ? '' : 'name';
  }
  async function saveName(name: string) {
    name = String(name || '').replace(/\s+/g, ' ').trim().slice(0, 16);
    if (name.length < 2) return;
    meta.name = name; if (acct) meta.nameBy = acct.user.uid; else delete meta.nameBy; saveMeta();
    await soloCall('/solo/name', { sid: deviceId(), name });
    view.nameSaved();
    if (view.tipOpen()) { closeTip(); toast(t('Saved')); }
    refreshBar();
    refreshBoards();
  }
  // your challenges: the newest first; the menu checks who's ahead
  const duels = () => (meta.duels = meta.duels || []);
  function rememberDuel(id: string, info: Partial<DuelRecord> = {}) {
    const list = duels(), i = list.findIndex((d) => d.id === id);
    const d: DuelRecord = i >= 0 ? list.splice(i, 1)[0] : { id, at: Date.now() };
    Object.assign(d, info);
    list.unshift(d); list.splice(20);
    saveMeta();
    return d;
  }
  const duelLink = (id: string) => location.origin + '/solo?c=' + id;
  function shareDuel(id: string, score: number) {
    const text = score ? t('I scored {n} on SongBuzz Solo. Same songs for you: can you beat me?', { n: fmt(score) }) : t('Play my SongBuzz challenge: same songs for everyone. Who knows more music?');
    T('solo_duel_share', {});
    const url = duelLink(id);
    if (navigator.share) navigator.share({ title: 'SongBuzz', text, url }).catch(() => {});
    else if (navigator.clipboard) navigator.clipboard.writeText(text + ' ' + url).then(() => toast(t('Link copied: send it to your friends'))).catch(() => toast(url, 6000));
    else toast(url, 6000);
  }
  // a new challenge: you play it first, then send it
  async function createDuel() {
    Sound.unlock();
    loading(t('Pressing your challenge…'), '#ff5f9e');
    try {
      const r = await fetch(api('/solo/duel'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sid: deviceId(), name: meta.name || null }) });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const { id }: { id: string } = await r.json();
      rememberDuel(id, { by: { name: meta.name || null }, mine: true });
      T('solo_duel_new', {});
      return playDuel(id, { by: { name: meta.name || null }, ghost: null, mine: true });
    } catch (e) {
      errorScreen(t("Couldn't create the challenge. Check the internet connection."), createDuel);
    }
  }
  // a challenge link: who sent it, the board so far, and a button to play (or the board, if you already did)
  async function openDuel(id: string): Promise<void> {
    loading(t('Opening the challenge…'), '#ff5f9e');
    const saved = loadSaved<SavedRun>(DUEL_KEY);
    if (saved && saved.run && saved.run.duel && saved.run.duel.id === id && !saved.run.over) { run = saved.run; deck.restore(saved.deck); return resume(); }
    let info: DuelInfo;
    try { info = await getJson<DuelInfo>(api('/solo/duel/' + id + '?sid=' + deviceId()), 8000); }
    catch (e) { return errorScreen(t('That challenge link doesn’t work. Ask for a new one.'), () => openDuel(id)); }
    rememberDuel(id, { by: info.by, played: !!info.me, mineHype: info.me ? info.me.hype : undefined });
    T('solo_duel_open', { played: !!info.me, players: info.n || 0 });
    ui = { screen: 'duel', id };
    setField('#ff5f9e');
    ui.info = info;
    view.draw('duel', { id, info, by: info.by && info.by.name ? info.by.name : t('A friend'), played: !!info.me });
  }
  async function playDuel(id: string, info: DuelStart) {
    Sound.unlock();
    loading(t('Pressing your challenge…'), '#ff5f9e');
    try {
      // the go starts on the server (a ticket, like the daily's), or this device has played it already: its board
      const tk = await soloCall<DuelTicket>('/solo/duel/' + id + '/start', { sid: deviceId() });
      if (tk && tk.ok && tk.j.already) return openDuel(id);
      // a challenge made from RULES.tourFrom is a whole tour, its songs from the tour's catalogue
      const made = String((info as { at?: string }).at || today()).slice(0, 10), full = made >= C.RULES.tourFrom;
      const songs = full ? await crateSongs('mixtape', null, 'duel:' + id) : await dailySongs('duel:' + id);
      await fillYears(songs.slice(0, 26), 8);
      run = C.newRun({ mode: 'duel', seed: 'duel:' + id, level: 6, full });
      if (made < C.RULES.setsFrom) run.noSets = true; // a challenge made before sets plays as it did for whoever made it
      run.duel = { id, by: info.by || null, mine: !!info.mine, token: (tk && tk.ok && tk.j.token) || null };
      run.ghost = info.ghost || null;
      deck.set(songs.filter((x) => x.year));
      T('solo_start', { mode: 'duel' });
      intro();
    } catch (e) {
      errorScreen(t("Couldn't load the challenge's songs. Check the internet connection."), () => playDuel(id, info));
    }
  }
  function loading(text: string, color?: string) {
    ui = { screen: 'loading' };
    setField(color || '#2a4bff');
    view.draw('loading', { text });
  }
  // the menu's challenge list: who's ahead on each (a new leader who isn't you is news)
  async function refreshDuels() {
    const list = duels().slice(0, 5);
    for (const d of list) {
      const b = await getJson<DuelInfo>(api('/solo/duel/' + d.id + '?sid=' + deviceId()), 6000).catch(() => null);
      if (!b) continue;
      const lead = b.top[0] || null;
      d.n = b.n; d.rank = b.me ? b.me.rank : null; d.mineHype = b.me ? b.me.hype : d.mineHype;
      d.lead = lead ? { name: lead.name, hype: lead.hype, me: !!lead.me } : null;
      d.news = !!(lead && !lead.me && b.me && d.seenLead !== lead.hype);
      saveMeta();
      if (ui.screen === 'menu') view.duelRow(d);
    }
  }
  const duelWho = (d: DuelRecord) => (d.by && d.by.name && !d.mine ? t('From {name}', { name: d.by.name }) : t('Your challenge'));
  const duelStatus = (d: DuelRecord) => (!d.mineHype && d.mineHype !== 0 ? t('Not played yet') : d.lead && !d.lead.me ? t('{name} leads with {n}', { name: d.lead.name || t('Anonymous'), n: fmt(d.lead.hype) }) : d.rank === 1 ? ((d.n || 1) > 1 ? t('You lead · {n} players', { n: d.n }) : t('You’re the first to play it')) : d.rank && d.n ? t('#{n} of {m}', { n: d.rank, m: d.n }) : t('Played: {n} points', { n: fmt(d.mineHype) }));
  
  // ---------------------------------------------------------------- menu
  function menu() {
    stopTimers(); Sound.stop(0.4);
    ui = { screen: 'menu' };
    setField('#2a4bff');
    const saved = loadSaved<SavedRun>(RUN_KEY), lv = level(), next = C.LEVELS[lv] || null, prev = C.LEVELS[lv - 1] || 0;
    const day = today(), D = meta.daily, done = dayGo(day), dsaved = loadSaved<SavedRun>(DAILY_KEY), dlive = dsaved && dsaved.run && dsaved.run.day === day && !dsaved.run.over;
    const streak = dailyStreak();
    const shelfN = Object.keys(meta.shelf).length;
    const cont = saved && saved.run && !saved.run.over ? saved.run : null;
    if (!run) checkChallenges(); // a streak or a shelf that grew elsewhere
    if (done) refreshStanding(day).then((s) => { if (s && ui.screen === 'menu') view.menuNews({ standing: standingText(s) }); });
    lastNight().then((y) => {
      if (ui.screen !== 'menu') return;
      view.menuNews({ yest: y || null, streak: dailyStreak() });
    });
    if (duels().length) refreshDuels();
    view.draw('menu', { lv, next, prev, day, D, done, dlive, streak, shelfN, cont });
  }
  // how yesterday's Daily ended for you, once everyone had played it: your place among them (asked once per visit)
  let lastNightP: Promise<LastNight | null> | null = null;
  function lastNight() {
    if (!cloud) return Promise.resolve(null);
    return (lastNightP = lastNightP || boardJson('daily', yesterday()).then((b) => {
      if (!b) return null;
      keepStreak(b);
      const me = b.me;
      if (!me) return null;
      const no = dayNo(yesterday());
      if (!me.rank) return { text: t('Yesterday’s gig #{no}: {n} points', { no, n: fmt(me.hype) }) };
      const top = b.n >= 10 ? Math.max(1, Math.ceil((100 * me.rank) / b.n)) : 0;
      return { trophy: true, text: t('Yesterday’s gig #{no}: you finished {rank} of {n}', { no, rank: '#' + me.rank, n: fmt(b.n) }) + (top && top <= 50 ? ' · ' + t('top {p}%', { p: top }) : '') };
    }).catch(() => null));
  }
  // Where a tour's music is known: this device's country by default, worldwide, or any other (kept per device). It
  // picks the songs (known there, every difficulty) and Home Grown's country. The Daily stays the same for everyone.
  const soloCountry = () => { const c = meta.country; return c === 'WORLD' || SongBuzzEngine.COUNTRIES.includes(c!) ? c! : detectCountry(); };
  // the countries on offer: quick picks, then the rest, those with their own music first
  function countryPicks(cc: string) {
    const home = detectCountry(), C2 = SongBuzzEngine.COUNTRIES, byName = (a: string, b: string) => countryName(a).localeCompare(countryName(b));
    const picks = [...new Set(['WORLD', ...(C2.includes(home) ? [home] : []), ...(C2.includes(cc) ? [cc] : [])])];
    return { picks, own: C2.filter((c) => SongBuzzEngine.LOCAL[c]).sort(byName), rest: C2.filter((c) => !SongBuzzEngine.LOCAL[c]).sort(byName) };
  }
  function setCountry(cc: string) {
    if (!(cc === 'WORLD' || SongBuzzEngine.COUNTRIES.includes(cc))) return;
    meta.country = cc; saveMeta(); T('solo_country', { country: cc });
    if (ui.screen === 'crates') crates();
  }
  // the highest stake beaten with a crate, as a record award (Balatro's stake stickers): Gold, Platinum, Diamond
  const stakeOf = (id: string) => { const st = meta.stake && meta.stake[id] != null ? meta.stake[id] : 0; return { st, S: C.STAKES[st] || C.STAKES[0] }; };
  // choose a crate (and a difficulty, once a tour is won)
  function crates() {
    if (ui.screen !== 'crates') T('solo_screen', { screen: 'crates' });
    ui = { screen: 'crates', stake: ui.stake || 0 };
    setField('#2a4bff');
    const lv = level(), cc = soloCountry();
    const list = Object.values(C.CRATES).filter((c) => !c.needsCountry || SongBuzzEngine.LOCAL[cc]);
    const maxStake = Math.max(0, ...Object.values(meta.stake || {}).map((x) => x + 1), 0);
    view.draw('crates', { lv, cc, list, maxStake });
  }
  
  // today's leaderboard
  async function dailyBoard() {
    if (ui.screen !== 'board') T('solo_screen', { screen: 'board' });
    ui = { screen: 'board' };
    setField('#2a4bff');
    await account();
    const day = today();
    view.draw('board', { day, d: dayGo(day) });
    refreshBoards();
  }
  function howto(back = 'menu') {
    if (ui.screen !== 'howto') T('solo_screen', { screen: 'howto' });
    ui = { screen: 'howto', back };
    setField('#2a4bff');
    const cards = [
      ['vinyl-record', t('Pick a record'), t('Each sleeve shows the decade and the genre. Gold, Hot, Neon and Lyric sleeves pay extra, with a catch: 5 seconds of song, sped up, no artists, or finish the lyric when the music stops.')],
      ['bell-ringing', t('Listen, then buzz'), t('The answers turn up after two seconds. Know it sooner? Buzz for +2 combo, then pick it within 3 seconds.')],
      ['lightning', t('Name it, fast'), t('Four answers. Fast answers give more points, and every song in a row raises your combo. Points × combo is your score.')],
      ['target', t('Then a dare'), t('After a right answer: on a gig’s first song, guess the year; after that, place the song on the gig’s timeline, before, between or after the songs you’ve played. The closer you get, the more it pays; only a guess way off costs you a little.')],
      ['shooting-star', t('A different set every gig'), t('From the second gig, you choose how the next one plays: name the song, who sings it, one-second intros, a non-stop medley, finish the lyric, or put songs in order on a timeline. Each comes with a prize. Once a gig, the crowd asks for something: give it to them for a bonus.')],
      ['megaphone', t('Beat the target'), t('Every gig has a score to beat, from 5 songs on the first night to 7 at the Stadium. Reach it by the fourth song or later and the gig is yours. Fall short on Night 1 and you play the gig again with new songs; from Night 2 the tour is over, unless you hold an Encore.')],
      ['smiley-sticker', t('Stick stickers'), t('Backstage, spend your cash on stickers that bend the rules. Five fit on your case. Find combos.')],
      ['sunglasses', t('Beat the Legends'), t('The last gig of every night is a Legend with a twist: songs backwards, through a wall, in 3-second blinks.')],
    ];
    view.draw('howto', { back, cards });
  }
  
  function shelf() {
    if (ui.screen !== 'shelf') T('solo_screen', { screen: 'shelf' });
    ui = { screen: 'shelf' };
    setField('#2a4bff');
    const by: Record<string, { id: string; title: string; artist: string; cover: string | null; year: number | null; n: number }[]> = {};
    for (const [id, x] of Object.entries(meta.shelf)) { const d = C.decadeOf(x[3]) || '?'; (by[d] = by[d] || []).push({ id, title: x[0], artist: x[1], cover: x[2], year: x[3], n: x[4] }); }
    const decs = Object.keys(by).sort((a, b) => (a === '?' ? 1 : b === '?' ? -1 : C.decadeStart(a) - C.decadeStart(b)));
    view.draw('shelf', { by, decs, n: Object.keys(meta.shelf).length });
  }
  
  function profile() {
    if (ui.screen !== 'profile') T('solo_screen', { screen: 'profile' });
    ui = { screen: 'profile' };
    setField('#2a4bff');
    const lv = level(), next = C.LEVELS[lv];
    const upcoming: Unlock[] = [];
    for (let l = lv + 1; l <= lv + 3 && l <= C.LEVELS.length; l++) for (const u of C.unlocksAt(l)) upcoming.push({ ...u, l });
    view.draw('profile', { lv, next, upcoming, ear: decadeStats() });
  }
  // your ear by decade: how often you name them, the best one called out
  function decadeStats() {
    const rows = Object.entries(meta.dec || {}).filter(([, x]) => x[1] >= 3).sort((a, b) => C.decadeStart(a[0]) - C.decadeStart(b[0]));
    if (!rows.length) return null;
    const best = rows.slice().sort((a, b) => b[1][0] / b[1][1] - a[1][0] / a[1][1])[0];
    return { best: best[0], bestPct: Math.round((100 * best[1][0]) / best[1][1]), rows: rows.map(([d, x]) => ({ d, pct: Math.round((100 * x[0]) / x[1]), color: DECADE_COLOR[d] || '#ffc531' })) };
  }
  function unlockInfo(u: Unlock) {
    if (u.kind === 'sticker') { const d = C.STICKERS[u.id]; const v = d.variant === 'decade' ? '80s' : 'Rock'; return { e: d.icon, name: C.stickerName({ id: u.id, v }, tv), kind: t('Sticker'), desc: C.stickerDesc({ id: u.id, v }, tv) }; }
    if (u.kind === 'tape') { const d = C.TAPES[u.id]; return { e: d.icon, name: t(d.name), kind: t('Tape'), desc: t(d.desc) }; }
    if (u.kind === 'legend') { const d = C.LEGENDS[u.id]; return { e: 'sunglasses', name: t(d.name), kind: t('Legend'), desc: t(d.desc) }; }
    const d = C.CRATES[u.id]; return { e: d.icon, name: t(d.name), kind: t('Crate'), desc: t(d.desc) };
  }
  
  // ---------------------------------------------------------------- starting
  async function startTour(crateId: string, stake = 0) {
    Sound.unlock();
    loading(t('Digging through the crate…'), '#2a4bff');
    try {
      const cc = soloCountry();
      const songs = await crateSongs(crateId, cc);
      run = C.newRun({ mode: 'tour', crate: crateId, level: level(), stake, country: cc, learn: !meta.runs });
      deck.set(songs);
      await fillYears(deck.peek(6));
      dropRun('tour');
      T('solo_start', { mode: 'tour', crate: crateId, stake, level: level(), country: cc });
      intro();
    } catch (e) {
      console.error(e);
      errorScreen(t("Couldn't load the songs. Check the internet connection."), () => startTour(crateId, stake));
    }
  }
  function dailyGate(day: string) {
    ui = { screen: 'dailygate' };
    setField('#ffc531');
    view.draw('dailygate', { day });
  }
  async function startDaily(opts: { unranked?: boolean } = {}) {
    Sound.unlock();
    const day = today(), saved = loadSaved<SavedRun>(DAILY_KEY);
    if (googleOn()) await account();
    if (dayGo(day)) return dailyBoard();
    if (saved && saved.run && saved.run.day === day && !saved.run.over) {
      run = saved.run; deck.restore(saved.deck);
      if (!run.token) { const tk = await dailyTicket(day); if (tk && tk.already) return playedElsewhere(day, tk); run.token = (tk && tk.token) || null; saveRun(); }
      return resume();
    }
    // only a go started signed in is ranked: say so before it starts, not after
    if (googleOn() && !(await account()) && !opts.unranked) return dailyGate(day);
    loading(t('Pressing today’s records…'), '#ffc531');
    try {
      const opts = C.dailyOpts(day), songs = opts.full ? await crateSongs('mixtape', null, 'daily:' + day) : await dailySongs(day);
      await fillYears(songs.slice(0, 26), 8);
      // the go starts now: one a day
      const tk = await dailyTicket(day);
      if (tk && tk.already) return playedElsewhere(day, tk);
      // a go kept from the server with no points was one it now hands back: it's this one
      if (meta.daily.days[day] && meta.daily.days[day].elsewhere) { delete meta.daily.days[day]; saveMeta(); }
      run = C.newRun(opts);
      run.token = (tk && tk.token) || null;
      deck.set(songs.filter((s) => s.year));
      // today's leader is the ghost to race
      const b = await boardJson('daily', day);
      run.ghost = b && b.ghost ? b.ghost : null;
      T('solo_start', { mode: 'daily', day: dayNo(day) });
      intro();
    } catch (e) {
      console.error(e);
      errorScreen(t("Couldn't load today's songs. Check the internet connection."), startDaily);
    }
  }
  // today's go was already used (another device, a cleared browser, or a go that was left unfinished): it counts
  function playedElsewhere(day: string, tk: DailyUsed) {
    meta.daily.days[day] = { hype: tk.hype || 0, grid: '', won: false, elsewhere: true, closed: true, by: meta.uid || null };
    saveMeta();
    toast(tk.moved ? t('You played today on this device before signing in, so today’s go isn’t ranked. Tomorrow’s will be.') : tk.finished ? t('You’ve played today’s gig. A new one comes tomorrow.') : t('You started today’s gig already: what you scored counts.'), 4000);
    refreshStanding(day);
    return dailyBoard();
  }
  function errorScreen(msg: string, retry: () => unknown) {
    ui = { screen: 'error', retry };
    view.draw('error', { msg });
  }
  // pick up a saved run where it stopped
  let fromHowto = false; // back from How to play in the pause menu: that song starts again, it isn't a walk-out
  function resume() {
    const howtoBack = fromHowto; fromHowto = false;
    Sound.unlock();
    // a run saved before sets: its gig plays Name that song, and a challenge under way goes on as it began
    if (run!.gig && !run!.gig.set) { run!.gig.set = 'name'; if (run!.mode === 'duel' && !run!.sets) run!.noSets = true; }
    const ph = run!.phase;
    if (ph === 'intro' || !run!.gig || run!.gig.offers) return intro();
    if (ph === 'shop') return shop();
    if (ph === 'cleared') return cleared();
    if (ph === 'over' || ph === 'won') return over();
    if (ph === 'play' || ph === 'bet' || ph === 'result') {
      // a song that was on: it plays again from the top (a bet or result that wasn't banked counts as banked)
      if (ph === 'bet') { C.bet(run!, null); return next(); }
      if (ph === 'result') return next();
      // in a Daily or a challenge a song is heard once: one left halfway (a reload, a closed tab) counts as missed
      const c0 = run!.gig!.cur as SongOn | null;
      // A tour gets one restart per song (the phone may have killed the tab), the second one counts as a miss
      const graceOk = !C.seeded(run!) && c0 && c0.heard && !howtoBack && !c0.restarted;
      if (graceOk) { c0.restarted = true; c0.heard = false; saveRun(); }
      else if (c0 && c0.heard && !howtoBack) {
        let res = C.answer(run!, false, { i: -1 });
        if (res && res.retry) res = C.answer(run!, false, { i: -1 }); // Second Chance doesn't get a go at it either
        if (res) {
          countDecade(c0.decade, false); rememberRecent(c0.song.id); saveRun();
          setField(fieldColor());
          toast(t('You left in the middle of a song, so it counts as a miss.'), 4000);
          return result(res as AnswerOutcome, c0, -1);
        }
      }
      return playSong();
    }
    return hand();
  }
  
  // ---------------------------------------------------------------- a gig: the intro
  // A Daily or a challenge starts with you picking one sticker of three (the same three for everyone): you know what it
  // does because you chose it
  function starterPick() {
    ui = { screen: 'starter' };
    setField(oneNightColor());
    view.draw('starter', { ch: run!.starterChoices! });
  }
  async function intro(): Promise<void> {
    stopTimers();
    if (run!.starterChoices && run!.starterChoices.length) return starterPick();
    if (run!.phase === 'intro' || !run!.gig) C.startGig(run!);
    if (run!.gig!.request === 'pending') await dealUp(); // the request comes from the first sleeves
    saveRun();
    ui = { screen: 'intro' };
    const g = run!.gig!, N = C.nightInfo(run!.night), L = g.legend;
    const daily = C.oneNight(run!), offers = offersShow();
    // a regular gig goes straight to the sleeves (its target and request on top); the first ones get a proper intro, and
    // so do a choice of sets and a set you haven't played before
    // the Daily's note: on an old one's every first gig, on a whole-tour one's very first
    const dailyNote = run!.mode === 'daily' && run!.gigNo === 0 && (daily || run!.night === 0);
    if (!L && !offers && meta.seen.intro && (!dailyNote || meta.seen.daily) && meta.seen['set:' + g.set]) return hand();
    g.introSeen = true; // the sleeves then skip the banner this intro already showed
    if (L) {
      setField('#141432');
      Sound.sfx('legend');
      view.draw('legend', { g, N, L, daily, twists: L.ids.map((id) => C.LEGENDS[id]) });
    } else {
      setField(fieldColor());
      const coach = offers ? (meta.seen.sets ? null : 'sets') : dailyNote && (daily || !meta.seen.daily) ? 'daily' : run!.gigNo === 0 && run!.night === 0 && !daily ? 'intro' : null;
      view.draw('intro', { g, N, daily, coach, first: run!.gigNo === 0 && run!.night === 0 && !run!.history.length, offers, set: g.set === 'name' || offers ? null : setShow(g.set, g.plays) });
    }
  }
  
  // ---------------------------------------------------------------- picking a record
  async function dealUp(fresh?: boolean) {
    if (deck.left() < 8) await refill();
    await fillYears(deck.peek(C.RULES.hand + 2)).catch(() => {});
    C.deal(run!, supply, fresh);
    saveRun();
    // start loading every sleeve's song now, so the one picked starts at once
    for (const h of run!.gig!.hand) Sound.load(h.song.id).catch(() => {});
  }
  const DARE_ICON: Record<string, string> = { fast: 'fast-forward', '5 seconds': 'timer', 'no artists': 'eye-slash', 'next line': 'microphone-stage' };
  // what your stickers like about a sleeve: their icons
  const sleeveLikes = (h: HandCard) => run!.stickers.filter((s) => (s.id === 'decadekid' && s.v === h.decade) || (s.id === 'genrehead' && s.v === h.family) || (s.id === 'purist' && run!.lastDecade === h.decade) || (s.id === 'timewarp' && run!.lastYear && h.decade && C.decadeStart(h.decade) > run!.lastYear)).map((s) => C.STICKERS[s.id].icon);
  const specialsDealt = () => run!.gig!.hand.some((h) => h.edition) && !C.gigRules(run!).blank;
  const oneNightColor = () => (run!.mode === 'duel' ? '#ff5f9e' : '#ffc531');
  // The field is the round, as in the party: a gig's is its set's colour (a Legend plays on the dark stage), and the choice
  // of the next set is on cobalt, like the party's pick of the next round
  const setColor = () => { const g = run!.gig; return g && !g.legend && !g.offers ? C.SETS[g.set].color : null; };
  const fieldColor = () => { const g = run!.gig; return g && g.legend ? '#141432' : g && g.offers ? '#2a4bff' : setColor() || (C.oneNight(run!) ? oneNightColor() : C.nightInfo(run!.night).color); };
  // ---------------------------------------------------------------- sets, the choice of one, the crowd
  const setPlays = (id: SetId, plays: number) => (id === 'artist' ? t('{n} songs · {s} s each', { n: plays, s: C.SETS[id].secs }) : id === 'medley' ? t('{n} songs, back to back', { n: plays }) : t('{n} songs', { n: plays }));
  function setShow(id: SetId, plays: number): SetShow {
    const S = C.SETS[id];
    return { id, name: t(S.name), desc: t(S.desc), color: S.color || (C.oneNight(run!) ? oneNightColor() : C.nightInfo(run!.night).color), icon: S.icon, line: setPlays(id, plays) };
  }
  function prizeText(p: Prize) {
    if (p.kind === 'cash') return t('+{p} if you clear it', { p: money(p.n || 0) });
    if (p.kind === 'skips') return t('+{n} skips', { n: p.n || 0 });
    return t('A free tape: {tape}', { tape: t(C.TAPES[p.id!].name) });
  }
  const PRIZE_ICON = { cash: 'coins', skips: 'arrows-clockwise', tape: 'cassette-tape' };
  function offersShow(): OfferShow[] | null {
    const g = run!.gig; if (!g || !g.offers) return null;
    return g.offers.map((o, i) => ({ ...setShow(o.set, o.plays), i, target: o.target, plays: o.plays, prize: prizeText(o.prize), prizeIcon: PRIZE_ICON[o.prize.kind] }));
  }
  const momentText = (m: { id: Moment['id']; v?: string | null }, key: 'name' | 'desc' | 'badge') => t(C.MOMENTS[m.id][key], { d: decLabel(m.v) });
  const momentShow = (m: Moment): MomentShow => ({ id: m.id, title: momentText(m, 'name'), desc: momentText(m, 'desc'), icon: C.MOMENTS[m.id].icon });
  // what the play screen asks
  function askLine() {
    const rules = C.gigRules(run!);
    return !ui.opts ? t('Cueing it up…') : ui.lyric ? t('Finish the lyric') : rules.ask === 'artist' ? t('Who sings it?') : rules.ask === 'slot' ? t('When is it from?') : rules.ear ? t('Name that intro') : t('Name that song');
  }
  // the Intro set's key: hear more of it, for fewer points
  function moreKey(): MoreKey | null {
    if (ui.screen !== 'play' || !ui.opts || ui.answered) return null;
    const rules = C.gigRules(run!), k = (ui.cur && ui.cur.ear) || 0;
    if (!rules.ear || k >= C.RULES.ear.length - 1) return null;
    const secs = C.RULES.earSecs[k + 1];
    return { label: secs ? t('Hear {n} s', { n: secs }) : t('Hear it all'), pts: '+' + C.RULES.ear[k + 1] };
  }
  function hearMore() {
    if (ui.screen !== 'play' || ui.answered || !ui.opts || frozen) return;
    const cur = run!.gig!.cur!, rules = C.gigRules(run!);
    if (!rules.ear || (cur.ear || 0) >= C.RULES.ear.length - 1) return;
    cur.ear = (cur.ear || 0) + 1; saveRun();
    Sound.sfx('pick'); haptic('light');
    Sound.play(cur.song.id, { ...C.gigRules(run!).fx }).catch(() => {});
    const keep = ui.started; renderPlay(); ui.started = keep;
  }
  // the crowd's moment, big, before the sleeves it's for
  function momentSplash() {
    const g = run!.gig; if (!g || !g.moment) return;
    Sound.sfx('crowd'); haptic('light');
    if (reduced()) return toast(momentText(g.moment, 'name') + ' ' + momentText(g.moment, 'desc'), 3500, C.MOMENTS[g.moment.id].icon);
    view.splash({ g, seeded: C.oneNight(run!), N: C.nightInfo(run!.night), moment: momentShow(g.moment) });
  }
  async function hand(fresh?: boolean): Promise<void> {
    stopTimers();
    if (run!.gig && run!.gig.offers) return intro(); // the set isn't chosen yet
    await dealUp(fresh);
    if (C.setOf(run!).hand === false) return medleyOn();
    ui = { screen: 'hand' };
    setField(fieldColor());
    const g = run!.gig!;
    const opening = g.played === 0 && !g.skipped && !g.legend && !g.introSeen;
    view.draw('hand', { g, opening });
    if (g.played === 0 && !g.splashed) { g.splashed = true; saveRun(); gigSplash(); }
    else if (g.moment && g.moment.live && !g.moment.seen) { g.moment.seen = true; saveRun(); momentSplash(); }
  }
  // The Golden Medley picks no records: the songs come one after another (after the gig's splash, the first)
  async function medleyOn() {
    const g = run!.gig!;
    setField(fieldColor());
    if (!C.pick(run!, 0)) return;
    saveRun();
    if (g.played === 0 && !g.splashed) {
      // the first song's screen, cueing, under the gig's splash; the music once the splash has gone
      g.splashed = true; saveRun();
      const key = 'medley:' + run!.plays;
      ui = { screen: 'play', cur: g.cur!, opts: null, started: 0, answered: false, revealed: false, buzzAt: null, lead: 0, lyric: null, key };
      renderPlay(); gigSplash();
      await wait(reduced() ? 300 : 2300);
      if (ui.key !== key) return;
    }
    playSong();
  }
  const lastSong = () => { const g = run && run.gig; return !!(g && !C.oneNight(run!) && g.played === g.plays - 1 && g.hype < g.target); };
  async function refill() {
    // a long tour runs through the crate: fetch more
    try { const more = await crateSongs(run!.crate, run!.country); const have = new Set(deck.all().map((s) => s.id)); deck.set(deck.peek(999).concat(more.filter((s) => !have.has(s.id)))); } catch (e) {}
  }
  
  // ---------------------------------------------------------------- a song
  let raf = 0, songTimer = 0;
  function stopTimers() { cancelAnimationFrame(raf); clearTimeout(songTimer); raf = 0; }
  // Pause (or a phone put away) holds the song and its clock where they are; Keep playing carries on from there
  let frozen: { at: number; key: string } | null = null; // {at, key}
  let pauseOpen = false; // the pause sheet is up: a song that starts meanwhile (the Medley's next) starts held
  function freeze() {
    if (ui.screen !== 'play' || ui.answered || frozen) return;
    frozen = { at: performance.now(), key: ui.key };
    cancelAnimationFrame(raf); raf = 0; Sound.hold(true);
  }
  function thaw(carryOn = true) {
    if (!frozen) return;
    const f = frozen; frozen = null;
    if (!carryOn) { stopTimers(); Sound.stop(0); }
    Sound.hold(false);
    if (carryOn && ui.screen === 'play' && ui.key === f.key && !ui.answered && ui.started) { ui.started += performance.now() - f.at; tick(); }
  }
  async function playSong() {
    const held = pauseOpen || document.visibilityState === 'hidden'; // paused, or put away, before this song began
    thaw(false); stopTimers();
    const g = run!.gig!, cur = g.cur!, rules = C.gigRules(run!), fx = rules.fx;
    ui = { screen: 'play', cur, opts: null, started: 0, answered: false, revealed: false, buzzAt: null, lead: 0, lyric: null, key: cur.song.id + ':' + run!.plays, named: rules.ask === 'slot' ? cur.song.title + ' · ' + cur.song.artist : null };
    const key = ui.key;
    if (held) { if (pauseOpen) freeze(); else pause(); } // its clock starts on Keep playing
    setField(fieldColor());
    renderPlay();
    let opts: AnswerOption[], pz: LyricPuzzle | null = null;
    const lyric = cur.edition === 'lyric';
    try {
      const [o, , p] = await Promise.all([rules.ask === 'slot' ? slotOpts(cur) : optionsFor(cur), Sound.load(cur.song.id), lyric ? getJson<LyricPuzzle | null>(api('/lyric/' + cur.song.id), 4500).catch(() => null) : null]);
      opts = o; pz = p && p.line && Array.isArray(p.decoys) && p.decoys.length >= 3 && p.cut > (p.from || 0) ? p : null;
    } catch (e) {
      if (ui.key !== key) return;
      toast(t("That record won't play. Pick another."));
      C.discard(run!); saveRun();
      return hand();
    }
    if (ui.key !== key) return;
    let play: PlayFx = fx;
    if (lyric && pz) {
      // Finish the Lyric: the lead-in plays, the music stops at the cut, and the answers are the next line
      const rnd = C.seeded(run!) ? seeded(run!.seed + ':lyric:' + cur.song.id) : Math.random;
      opts = shuffleWith(rnd, [{ line: pz.line, right: true }, ...pz.decoys.slice(0, 3).map((d) => ({ line: d }))]);
      ui.lyric = pz; ui.lead = Math.max(1, pz.cut - (pz.from || 0));
      play = { ...fx, from: pz.from || 0, stopAt: pz.cut };
    } else if (lyric) {
      // no lyric to finish for this one: it plays as a Neon sleeve (×2, no artists), so the dare still stands
      cur.edition = 'neon'; saveRun();
    } else if (cur.song.chorus != null && !rules.ear && (rules.medley || (!C.oneNight(run!) && run!.night <= 2)) && !fx.reverse && !fx.loop) play = { ...fx, from: cur.song.chorus }; // the first three nights (and the Medley's six seconds) drop the needle on the hook, so the Theatre's closer answers don't come with intros too; the Intro set starts at the top
    ui.opts = opts;
    if (rules.noBuzz && !ui.lead) { ui.revealed = true; ui.revealedAt = 0; } // nothing to buzz for: the answers are up from the start
    try { await Sound.play(cur.song.id, play); } catch (e) { C.discard(run!); saveRun(); toast(t("That record won't play. Pick another.")); return hand(); }
    if (ui.key !== key) return;
    ui.started = performance.now();
    if (frozen) frozen.at = ui.started; // paused while it cued up: its clock starts on Keep playing
    if (!cur.heard) { cur.heard = true; saveRun(); } // a reload won't play it again for free (see resume)
    renderPlay();
    tick();
  }
  function renderPlay() {
    const cur = ui.cur!;
    if (cur.fifty && ui.opts && !ui.fiftyHidden) { const wrong = ui.opts.map((o, i) => (o.right ? -1 : i)).filter((i) => i >= 0); ui.fiftyHidden = shuffleWith(Math.random, wrong).slice(0, 2); }
    view.draw('play', {});
  }
  // the answers a tape or a 50/50 took away
  function hiddenOpts() { const hidden = new Set(ui.cur!.hidden || []); (ui.fiftyHidden || []).forEach((i) => hidden.add(i)); return hidden; }
  // the answers turn face up: by themselves after a moment, or now because you buzzed (then the clock is short)
  function reveal(buzz: boolean) {
    if (ui.screen !== 'play' || ui.revealed || !ui.opts || ui.answered || frozen) return;
    ui.revealed = true; ui.revealedAt = performance.now();
    if (buzz) { ui.buzzAt = (performance.now() - ui.started) / 1000; Sound.sfx('buzz'); haptic('light'); seen('play'); }
    view.revealed(buzz);
  }
  function tick() {
    const rules = C.gigRules(run!), lead = ui.lead || 0, total = rules.answerSecs + lead;
    let ticked = 4; // the last three seconds are heard, each a little higher
    const loop = () => {
      if (ui.screen !== 'play' || ui.answered || frozen) return;
      const B = C.RULES.buzz, s = (performance.now() - ui.started) / 1000;
      const rv = lead || (rules.noBuzz ? 0 : B.reveal); // a Lyric sleeve's answers turn up when the music stops
      if (!ui.revealed) { if (s >= rv) reveal(false); else view.revealBar(1 - s / rv); }
      // after a buzz the clock is the buzz window
      const win = ui.buzzAt != null ? B.window - (s - ui.buzzAt) : Infinity;
      const left = Math.max(0, Math.min(total - s, win)), of = win < Infinity ? B.window : total;
      const speed = rules.ear ? C.RULES.ear[Math.min(C.RULES.ear.length - 1, (ui.cur && ui.cur.ear) || 0)] : rules.noSpeed ? 0 : Math.round(C.RULES.speedChips * Math.max(0, C.RULES.speedSecs - Math.max(0, s - lead)));
      view.clock({ part: left / of, low: win < Infinity || left < 4, speed, secs: Math.ceil(left) });
      if (left <= 0) return onAnswer(-1);
      if (Math.ceil(left) < ticked) { ticked = Math.ceil(left); Sound.sfx('tick', (3 - ticked) * 4); }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
  }
  
  // an answer (i = -1: time ran out)
  async function onAnswer(i: number) {
    if (ui.screen !== 'play' || ui.answered || !ui.opts || (i >= 0 && !ui.revealed) || frozen) return;
    const tsec = Math.max(0, (performance.now() - ui.started) / 1000 - (ui.lead || 0)); // a lyric's clock starts at the cut
    const right = i >= 0 && !!ui.opts[i].right;
    const res = C.answer(run!, right, { t: tsec, i, buzz: ui.buzzAt != null });
    if (!res) return;
    if (res.retry) {
      // Second Chance: that one's out, try again
      haptic('error'); Sound.sfx('wrong');
      ui.wrongs = (ui.wrongs || []).concat(i);
      view.wrongOpt(i);
      toast(t('Second Chance: try again'));
      return;
    }
    ui.answered = true;
    stopTimers();
    seen('play'); seen('pick'); if (cur0Edition()) seen('specials');
    const g = run!.gig!, cur = g.cur!;
    // light up the right answer (and the wrong pick)
    view.answered(i, right ? t('Right!') : i < 0 ? t('Time’s up!') : t('Not quite'));
    if (right) { haptic('success'); Sound.sfx('right'); } else { haptic('error'); Sound.sfx('wrong'); }
    if (Object.keys(C.gigRules(run!).fx).length || ui.lead) Sound.straighten(cur.song.id);
    if (right && C.gigRules(run!).ask !== 'slot') addToShelf(cur.song); // the Timeline set names it for you
    countDecade(cur.decade, right);
    rememberRecent(cur.song.id);
    saveRun();
    await wait(right ? 650 : 900);
    result(res, cur, i);
  }
  function cur0Edition() { return run!.gig && run!.gig.cur && run!.gig.cur.edition; }
  function addToShelf(s: Song) {
    const x = meta.shelf[s.id];
    meta.shelf[s.id] = [s.title, s.artist, s.cover || null, s.year || null, ((x && x[4]) || 0) + 1];
    meta.songs++;
    saveMeta();
  }
  // how you do per decade, across every run (the profile shows it)
  function countDecade(dec: string | null, right: boolean) {
    if (!dec) return;
    meta.dec = meta.dec || {};
    const x = meta.dec[dec] || (meta.dec[dec] = [0, 0]);
    x[1]++; if (right) x[0]++;
    saveMeta();
  }
  function rememberRecent(id: SongId) { meta.recent = meta.recent.filter((x) => x !== id).concat(id).slice(-500); saveMeta(); }
  
  // ---------------------------------------------------------------- the result: Chips × Mult, then the year bet
  async function result(res: AnswerOutcome, cur: CurrentSong, picked: number) {
    ui = { screen: 'result', res, cur };
    const song = cur.song;
    if (!res.right) {
      const lost = res.lostStreak ? t('Streak of {n} lost: the next song is back to ×1', { n: res.lostStreak }) : res.saved ? t('Safety Net: your streak is safe') : '';
      view.draw('missed', { song, verdict: picked < 0 ? t('Time’s up! It was') : t('Not quite. It was'), lost, glass: !!res.glass && run!.gig!.hype < run!.gig!.target, hud: hudOf() });
      return settle();
    }
    // a right answer: count it up, step by step
    view.draw('tally', { song, res, hud: hudBefore(res) });
    // Balatro's way: one thing at a time. Each step says what scored and pops its number off the thing that did it
    // (a sticker in your case, the points or the combo box), the box jumps, the sound climbs; then a beat, and the
    // total counts up and slams in (on fire when it's a big one). A tap speeds it all up.
    let chips = 0, mult = res.simple ? 0 : 1, k = 0, xk = 0;
    ui.counting = true; ui.ff = C.setOf(run!).hand === false; // the Medley doesn't stop for the count
    const speed = () => (meta.fastTally ? 0.5 : 1); // the 1× / 2× pill (Balatro's game speed), kept per device
    const pace = () => (ui.ff || reduced() ? 70 : Math.round(Math.max(280, 600 - k * 40) * speed()));
    for (const st of res.steps!) {
      if (ui.screen !== 'result') return;
      let label = '', cls = '', amt = '';
      const who = st.src === 'sticker' ? C.stickerName(run!.stickers.find((s) => s.id === st.id) || { id: st.id }, tv) : st.src === 'base' ? (C.gigRules(run!).ask === 'slot' ? t('Right spot') : t('Named it')) : st.src === 'speed' ? t('Speed') : st.src === 'streak' ? (st.n === 1 ? t('First of a streak') : t('{n} in a row', { n: st.n })) : st.src === 'hot' ? t('Hot') : st.src === 'buzz' ? t('Buzzed!') : st.src === 'neon' ? t('Neon') : st.src === 'lyric' ? t('Lyric') : st.src === 'hypeshot' ? t('Booster') : st.src === 'request' ? t('Request: {d}', { d: decLabel(st.v) }) : st.src === 'ear' ? (st.n === 1 ? t('Named in a second') : t('Named in {n} seconds', { n: st.n })) : st.src === 'line' ? t('Timeline') : st.src === 'medley' ? t('Medley') : st.src === 'moment' ? (st.id === 'decade' ? t('The crowd: {d}', { d: decLabel(st.v) }) : st.id === 'chant' ? t('The crowd: buzzed!') : t('Lights out')) : (st as ScoreStep).src;
      if (st.chips) { chips += st.chips; amt = '+' + fmt(st.chips); label = amt + ' ' + who; cls = 'c'; Sound.sfx('chips', k); }
      if (st.mult) { mult += st.mult; amt = '+' + fmtMult(st.mult) + ' ' + t('Combo'); label = '+' + st.mult + ' ' + t('Combo') + ' · ' + who; cls = 'm'; Sound.sfx('tick', k); }
      if (st.x) { mult *= st.x; amt = '×' + fmtMult(st.x); label = '×' + fmtMult(st.x) + ' · ' + (st.again ? '' : who); cls = 'x'; Sound.sfx('xmult', xk++); haptic('light'); }
      view.tallyStep({ st, who, chips, mult, amt, label, cls });
      k++;
      await wait(pace());
    }
    view.tallyMult(res.mult!);
    // a beat, then the total: points × combo, counted up and slammed in
    await wait(ui.ff || reduced() ? 60 : 380 * speed());
    if (ui.screen !== 'result') return;
    view.tallySum(chips, res.mult!);
    const big = (run!.gig && run!.gig.target && !C.oneNight(run!) ? res.hype! >= run!.gig.target * 0.3 : res.hype! >= 150) || res.mult! >= 4;
    view.tallyCharge();
    {
      const dur = ui.ff || reduced() ? 120 : Math.min(1100, 450 + res.steps!.length * 60) * speed(), t0 = performance.now();
      let ticks = 0;
      await new Promise<void>((done) => { const step = () => { const f = Math.min(1, (performance.now() - t0) / dur), e = 1 - Math.pow(1 - f, 2); view.tallyTotal(Math.round(res.hype! * e)); if (f * 12 > ticks) { Sound.sfx('chips', ticks); ticks++; } if (f < 1) requestAnimationFrame(step); else done(); }; requestAnimationFrame(step); });
      view.tallyLanded(big, res.hype!);
    }
    ui.counting = false;
    if (res.cash.length) view.tallyCash(res.cash.map((c) => '+' + money(c.cash)).join(' '));
    Sound.sfx('total'); haptic('success');
    if (big) Sound.sfx('crowd');
    if (res.cash.length) { Sound.sfx('cash'); view.cash(); }
    await wait(ui.ff || reduced() ? 150 : 700 * speed());
    if (ui.screen !== 'result') return;
    // no bet (a first tour's first gig, or a song without a year): the points go straight in
    if (run!.phase !== 'bet' || !cur.song.year || !cur.decade) { if (run!.phase === 'bet') C.bet(run!, null); return betDone({ verdict: 'bank', hype: res.hype!, year: null }); }
    // the bet takes the place of the steps, under the tally: the song and its score stay where they were
    view.dare(); // the song and its score shrink so the dare fits
    betPanel(res, cur);
  }
  const fmtMult = (m: number) => { const k = m < 10 ? 100 : 10; return (Math.round(m * k) / k).toLocaleString(LANG === 'es' ? 'es-ES' : 'en-US'); }; // ×1.25 and ×0.75 as they are
  // The score bar as it was before this song: a song with no year bet is already in, and the bar mustn't give the
  // total away before it's counted (it runs up once the total lands)
  function hudBefore(res: AnswerOutcome) {
    const g = run!.gig; if (!g) return hudOf();
    const banked = run!.phase !== 'bet', streak = run!.streak;
    if (banked) { g.hype -= res.hype!; run!.totalHype -= res.hype!; }
    if (res.right && streak > 0) run!.streak = streak - 1; // this song's combo, not the next one's
    try { return hudOf(); } finally { if (banked) { g.hype += res.hype!; run!.totalHype += res.hype!; } run!.streak = streak; }
  }
  // a tap while the score counts up: the rest of it goes quickly
  const hurryTally = () => { if (ui.screen === 'result' && ui.counting) ui.ff = true; };
  
  let betTimer = 0;
  const BET_SECS = 7;
  function betClock() {
    const t0 = performance.now();
    const loop = () => {
      if (ui.screen !== 'result' || ui.betDone) return;
      const f = Math.max(0, 1 - (performance.now() - t0) / (BET_SECS * 1000));
      view.betBar(f);
      if (f <= 0) return onBet(null);
      betTimer = requestAnimationFrame(loop);
    };
    betTimer = requestAnimationFrame(loop);
  }
  function betPanel(res: AnswerOutcome, cur: CurrentSong) {
    const song = cur.song;
    const decade = cur.decade;
    if (!song.year || !decade) { C.bet(run!, null); return betDone({ verdict: 'bank', hype: res.hype! }); }
    if (C.betKind(run!) === 'timeline') return timelinePanel(res, cur);
    const start = C.decadeStart(decade), now = new Date().getFullYear();
    // from Night 2: a year near a decade's edge gets a window around it, so the edge doesn't give it away; the right
    // year sits anywhere in it (not always the 5th button)
    const edge = !C.oneNight(run!) && run!.night >= 1 && (song.year - start <= 1 || song.year - start >= 8);
    let from = edge ? song.year - (2 + Math.floor(Math.random() * 6)) : start;
    if (edge && from + 9 > now) from = now - 9;
    const years: number[] = []; for (let y = from; y < from + 10 && y <= now; y++) years.push(y);
    const hr = C.has(run!, 'highroller'), ex = hr ? 3 : C.RULES.bet.exact, cl = hr ? 2 : C.RULES.bet.close, rng = C.has(run!, 'almanac') ? 2 : 1;
    const need = run!.gig!.target - run!.gig!.hype, clears = !C.oneNight(run!) && need > 0 && res.hype! >= need;
    const boot = C.has(run!, 'bootlegger');
    view.betYears({ res, years, song, seen: cur.crystal ? song.year : null, odds: betOdds(res, ex, cl, rng, boot), clears, keep: clears ? t('Keep {n}: that clears the gig!', { n: fmt(res.hype!) }) : t('Keep my {n} points', { n: fmt(res.hype!) }) });
    betClock();
  }
  // What the year bet pays, in a line: the closer, the more; only a long way off costs anything
  function betOdds(res: AnswerOutcome, ex: number, cl: number, rng: number, boot: boolean) {
    const soft = C.softBets(run), B = C.RULES.bet;
    if (!soft) return boot ? t('Spot on ×{a} = {x} · within {r} ×{b} = {y} · wrong: you keep {z}', { a: ex, x: fmt(res.hype! * ex), r: rng === 1 ? t('a year') : t('{n} years', { n: rng }), b: fmtMult(cl), y: fmt(res.hype! * cl), z: fmt(res.hype!) })
      : t('Spot on ×{a} = {x} · within {r} ×{b} = {y} · wrong: half, {z}', { a: ex, x: fmt(res.hype! * ex), r: rng === 1 ? t('a year') : t('{n} years', { n: rng }), b: fmtMult(cl), y: fmt(res.hype! * cl), z: fmt(Math.round(res.hype! * B.oldMiss)) });
    // what each guess pays, as chips from the best to the worst (the first time too: a row to read, not a sentence)
    const chip = (tx: string, cls = '') => `<span class="oc ${cls}">${tx.replace(/&/g, '&amp;').replace(/</g, '&lt;')}</span>`;
    return [
      chip(t('Spot on ×{a} = {x}', { a: fmtMult(ex), x: fmt(Math.round(res.hype! * ex)) }), 'best'),
      chip(rng === 1 ? t('1 year off ×{b}', { b: fmtMult(cl) }) : t('within {n} years ×{b}', { n: rng, b: fmtMult(cl) }), 'good'),
      rng === 1 ? chip(t('2 years ×{c}', { c: fmtMult(B.two) }), 'good') : '',
      chip(t('3 off: you keep them')),
      chip(boot ? t('further: you keep them') : t('4+ off: half'), boot ? '' : 'bad'),
    ].join('');
  }
  // Timeline: before, between or after the songs played in this gig (the party game's round, as a dare)
  const slotLine = (tl: TimelineCard[], i: number) => (i === 0 ? t('Before {y}', { y: tl[0].year }) : i === tl.length ? t('After {y}', { y: tl[tl.length - 1].year }) : tl[i - 1].year + ' – ' + tl[i].year);
  function timelinePanel(res: AnswerOutcome, cur: CurrentSong) {
    const tl = C.timelineCards(run!), O = C.timelineOdds(run!), right = C.timelineSlot(run!);
    const need = run!.gig!.target - run!.gig!.hype, clears = !C.oneNight(run!) && need > 0 && res.hype! >= need;
    const wrong = Math.round(res.hype! * O.wrong);
    // the gentler rules (tours now, the Daily from RULES.softBets): a wrong spot ×0.75; one off keeps with 3+ spots
    const odds = !C.softBets(run) ? (!meta.seen.timeline ? t('<b>Before, between or after</b> this gig’s other {d} songs? Right spot ×{a}, wrong: half.', { d: decLabel(cur.decade), a: fmtMult(O.right) })
      : t('Right spot ×{a} = {x} · wrong: half, {z}', { a: fmtMult(O.right), x: fmt(Math.round(res.hype! * O.right)), z: fmt(wrong) }) + (O.near ? ' · ' + t('one spot off: ×{b}', { b: fmtMult(O.near) }) : '') + (O.wrong === 1 ? ' · ' + t('Bootlegger: a wrong spot keeps your points') : ''))
      : !meta.seen.timeline ? t('<b>Before, between or after</b> this gig’s other {d} songs? Right spot ×{a}; a wrong spot ×{w}.', { d: decLabel(cur.decade), a: fmtMult(O.right), w: fmtMult(O.wrong) }) + (O.keepOne ? ' ' + t('One spot off, you keep your points.') : '')
      : [t('Right spot ×{a} = {x}', { a: fmtMult(O.right), x: fmt(Math.round(res.hype! * O.right)) }), O.near ? t('one spot off: ×{b}', { b: fmtMult(O.near) }) : O.keepOne ? t('one spot off: you keep them') : '', O.wrong === 1 ? t('Bootlegger: a wrong spot keeps your points') : O.keepOne || O.near ? t('further: ×{d}', { d: fmtMult(O.wrong) }) : t('wrong: ×{d}', { d: fmtMult(O.wrong) })].filter(Boolean).join(' · ');
    view.betTimeline({ res, cur, tl, ask: t('Place it among the {d}', { d: decLabel(cur.decade) }), odds, seen: cur.crystal ? right : null, clears, keep: clears ? t('Keep {n}: that clears the gig!', { n: fmt(res.hype!) }) : t('Keep my {n} points', { n: fmt(res.hype!) }) });
    betClock();
  }
  function onBet(year: number | null) {
    if (ui.screen !== 'result' || ui.betDone) return;
    ui.betDone = true;
    ui.guess = year;
    cancelAnimationFrame(betTimer);
    const kind = C.betKind(run!);
    seen(kind === 'timeline' ? 'timeline' : 'bet');
    const r = C.bet(run!, year);
    if (!r) return;
    saveRun();
    view.betMarked(r, year);
    if (r.verdict === 'exact') { Sound.sfx('exact'); haptic('success'); confetti(40); }
    else if (r.verdict === 'close') { Sound.sfx('right'); haptic('success'); }
    else if (r.verdict === 'kept') { Sound.sfx('pick'); haptic('light'); }
    else if (r.verdict === 'lost') { Sound.sfx('wrong'); haptic('error'); }
    betDone(r);
  }
  function betDone(r: BetShown) {
    const guess = ui.guess;
    if (r.year == null) { const M0 = meterOf(); view.hypeCount(M0.now - r.hype, M0.now); setTimeout(settle, 200); return; }
    const tl = r.kind === 'timeline';
    const line = tl ? {
      exact: ['ok', 'check', t('Right spot! It came out in {y}', { y: r.year }), t('Your points ×{x}: {n}', { x: fmtMult(r.x), n: fmt(r.hype) })],
      close: ['ok', 'check', r.slot === r.right ? t('Right spot! It came out in {y}', { y: r.year }) : t('One spot off: it came out in {y}', { y: r.year }), t('Your points ×{x}: {n}', { x: fmtMult(r.x), n: fmt(r.hype) })],
      kept: ['bank', 'check', r.slot != null && Math.abs(r.slot - r.right!) === 1 ? t('One spot off: it came out in {y}', { y: r.year }) : t('Wrong spot: it came out in {y}', { y: r.year }), r.slot != null && Math.abs(r.slot - r.right!) === 1 ? t('Close enough: you keep your {n} points', { n: fmt(r.hype) }) : t('Bootlegger: you keep {n}', { n: fmt(r.hype) })],
      lost: ['no', 'cross', t('Wrong spot: it came out in {y}', { y: r.year }), r.x === 1 ? t('Bootlegger: you keep {n}', { n: fmt(r.hype) }) : r.x === C.RULES.bet.oldMiss ? t('Half this song’s points: {n}', { n: fmt(r.hype) }) : t('Way off: ×{x}, {n} points', { x: fmtMult(r.x), n: fmt(r.hype) })],
      bank: ['bank', 'check', t('Points kept. It came out in {y}', { y: r.year }), t('{n} points', { n: fmt(r.hype) })],
    }[r.verdict] : {
      exact: ['ok', 'check', t('Spot on! It came out in {y}', { y: r.year }), t('Your points ×{x}: {n}', { x: fmtMult(r.x), n: fmt(r.hype) })],
      close: ['ok', 'check', t('Close! It came out in {y}, you said {g}', { y: r.year, g: guess }), t('Your points ×{x}: {n}', { x: fmtMult(r.x), n: fmt(r.hype) })],
      kept: ['bank', 'check', t('It came out in {y}, you said {g}', { y: r.year, g: guess }), Math.abs(guess! - r.year) <= C.RULES.bet.keep ? t('Not far off: you keep your {n} points', { n: fmt(r.hype) }) : t('Bootlegger: you keep {n}', { n: fmt(r.hype) })],
      lost: ['no', 'cross', t('Wrong year: it came out in {y}, you said {g}', { y: r.year, g: guess }), r.x === 1 ? t('Bootlegger: you keep {n}', { n: fmt(r.hype) }) : r.x === C.RULES.bet.oldMiss ? t('Half this song’s points: {n}', { n: fmt(r.hype) }) : t('Way off: ×{x}, {n} points', { x: fmtMult(r.x), n: fmt(r.hype) })],
      bank: ['bank', 'check', t('Points kept. It came out in {y}', { y: r.year }), t('{n} points', { n: fmt(r.hype) })],
    }[r.verdict];
    // the years stay, with the real one in green (and your guess in red when wrong); the question becomes the answer
    view.betLine({ kind: line[0], mark: line[1] as IconName, head: line[2], sub: line[3] + (r.cash ? ' · +' + money(r.cash) : ''), off: !tl && guess != null && r.year ? Math.abs(guess - r.year) : 0 });
    // the sum says why the total changed: points × combo = total, then the year's ×2 (or ½)
    view.betTotal(r.verdict !== 'bank' && r.x !== 1 ? t('Total ×{x}', { x: fmtMult(r.x) }) : null, r.verdict !== 'bank' && r.verdict !== 'kept' ? [ui.res!.hype!, r.hype] : null);
    if (r.verdict === 'kept') view.betFlash('bank', tl ? t('One spot off') : t('Not far off'), t('Points kept'));
    else if (r.verdict !== 'bank') view.betFlash(line[0], r.verdict === 'lost' ? (tl ? t('Wrong spot') : t('Wrong year')) : tl ? (r.slot === r.right ? t('Right spot!') : t('One spot off')) : r.verdict === 'exact' ? t('Spot on!') : t('Close!'), r.verdict === 'lost' ? (r.x === C.RULES.bet.oldMiss ? t('It was {y} · half the points', { y: r.year }) : t('It was {y} · ×{x}', { y: r.year, x: fmtMult(r.x) })) : '×' + fmtMult(r.x));
    // the points go in
    const M = meterOf();
    view.hypeCount(M.now - r.hype, M.now);
    view.hudIn(M); // "faltan 146", not the old "faltan 220" next to the new score
    if (r.cash) view.cash();
    setTimeout(settle, r.verdict === 'bank' ? 300 : 900);
  }
  
  // After a song's result: the next sleeves right there (one tap moves on and plays), or the way out of the gig.
  async function settle() {
    stopTimers(); cancelAnimationFrame(betTimer);
    if (ui.settled) return;
    if (ui.res && ui.res.right) seen('tally');
    (ui as Complete<ResultUi>).settled = true;
    const r = C.afterSong(run!);
    (ui as Complete<ResultUi>).outcome = r;
    saveRun();
    if (run!.mode === 'daily' && (r === 'next' || r === 'encore' || r === 'cleared')) postDaily(run!, false);
    checkChallenges();
    if (r === 'encore') encoreFlash();
    rivalMoment();
    view.hud();
    refreshBar(); // an encore used, cash won
    const go = r === 'next' || r === 'encore';
    if (!go) { // a gig over: how it went (the big verdict that says so can be tapped past)
      const g = run!.gig, c = run!.cleared, win = r === 'cleared' || r === 'won';
      T('solo_gig', { ...runAt(), result: r, hype: win && c ? c.hype : g ? g.hype : run!.totalHype, target: win && c ? c.target : g ? g.target : 0,
        set: g && !g.legend ? g.set : '', legend: g && g.legend ? (g.legend.headliner ? 'headliner' : g.legend.ids.join(',')) : '', songs: g ? g.plays : 0, cash: run!.cash });
    }
    const label = go ? t('Next song') : r === 'replay' ? t('Play it again') : r === 'cleared' ? (C.oneNight(run!) ? t('Next gig') : run!.cleared && run!.cleared.legend ? t('Legend beaten!') : t('Gig cleared!')) : r === 'won' && !C.oneNight(run!) ? t('You won the tour!') : C.oneNight(run!) && r === 'won' ? t('See your score') : t('The crowd goes home…');
    view.nextKey(label, go ? 'to-hand' : r === 'cleared' ? 'to-cleared' : r === 'replay' ? 'to-replay' : 'to-over', r === 'failed' || r === 'replay');
    if (go) {
      dealUp().catch(() => {}); // the next sleeves, ready for the next screen
      if (C.setOf(run!).hand === false) { const u = ui; setTimeout(() => { if (ui === u && ui.screen === 'result') hand(); }, reduced() ? 500 : 1100); } // the Medley rolls on
    } else { const u = ui; clearTimeout(verdictTimer); verdictTimer = setTimeout(() => { if (ui === u && ui.screen === 'result') verdict(r); }, 900); } // the gig is over: say so, big (unless they already went on)
  }
  let verdictTimer = 0;
  function encoreFlash() {
    Sound.sfx('crowd'); haptic('success');
    view.encoreFlash();
  }
  // resuming a saved run in the middle of a result
  function next() {
    stopTimers(); cancelAnimationFrame(betTimer);
    const r = C.afterSong(run!);
    saveRun();
    if (r === 'next' || r === 'encore') return hand();
    if (r === 'cleared') return cleared();
    if (r === 'replay') return intro();
    return over();
  }
  
  // ---------------------------------------------------------------- a gig cleared: the cash
  async function cleared() {
    const fromVerdict = view.verdictOpen();
    clearTimeout(verdictTimer); view.closeVerdict();
    ui = { screen: 'cleared' };
    Sound.stop(1.2);
    setField('#ffc531');
    const c = run!.cleared!;
    if (!fromVerdict) { confetti(); Sound.sfx('fanfare'); haptic('success'); }
    checkChallenges();
    const names: Record<string, string> = { 'Gig cleared': t('Gig cleared'), 'Legend beaten': t('Legend beaten'), 'Songs to spare': t('Songs to spare'), 'Skips to spare': t('Skips to spare'), Interest: t('Savings'), 'Set prize': t('Set prize') };
    const seeded = C.oneNight(run!), spare = Math.max(0, c.hype - c.target);
    const nextSlot = run!.gigNo === 2 ? t('Night {n} · {venue}', { n: run!.night + 2, venue: t(C.nightInfo(run!.night + 1).name) }) : run!.gigNo === 1 ? t('The Legend') : gigName(run!.gigNo + 1);
    view.draw('cleared', { c, seeded, spare, names, nextSlot });
    for (let k = 0, n = c.lines.length + 2; k < n; k++) { // what you had, what the gig paid, the cash
      await wait(380);
      if (ui.screen !== 'cleared') return;
      view.clearedRow(k);
      Sound.sfx('cash');
    }
    view.clearedDone();
  }
  
  // ---------------------------------------------------------------- backstage: the shop
  function shop() {
    if (run!.phase !== 'shop') C.openShop(run!);
    saveRun();
    ui = { screen: 'shop' };
    setField('#fff6e3');
    const sh = run!.shop!, nextNight = run!.gigNo === 2 ? run!.night + 1 : run!.night, nextGig = run!.gigNo === 2 ? 0 : run!.gigNo + 1;
    const nextTarget = C.targetFor(run!, nextNight, nextGig);
    const full = run!.stickers.length >= C.RULES.stickerSlots;
    // the Legend at the end of tonight (or, after one, of the next night): known now, so you can shop for it
    const LN = C.legendFor(run!, run!.gigNo === 2 ? run!.night + 1 : run!.night);
    view.draw('shop', { sh, nextNight, nextGig, nextTarget, full, LN, showLegend: !(run!.learn && run!.night === 0 && run!.gigNo < 2) });
  }
  function offerTip(kind: ShopKind, i: number) {
    const list = kind === 'tape' ? run!.shop!.tapes : run!.shop!.items, it = list[i];
    if (!it || it.sold) return;
    const d = kind === 'tape' ? C.TAPES[it.id] : C.STICKERS[it.id];
    const tx = kind === 'tape' ? { name: t(d.name), desc: t(d.desc) } : stickerTxt(it);
    const full = kind === 'tape' ? run!.tapes.length >= C.RULES.tapeSlots : run!.stickers.length >= C.RULES.stickerSlots;
    const poor = run!.cash < it.price;
    openTip('offer', { kind, i, it, d, tx, full, poor });
  }
  
  // ---------------------------------------------------------------- tips: a sticker or tape up close
  // A sticker, a tape, the pause menu: a sheet from the bottom on a phone, a dialog in the middle on a computer.
  function openTip<K extends keyof Tips>(kind: K, d: Tips[K]) { closeTip(true); view.tip(kind, d); }
  function closeTip(instant?: boolean) { tipFor = null; pauseOpen = false; thaw(); view.closeTip(instant); }
  function stickerTip(i: number) {
    const s = run!.stickers[i]; if (!s) return;
    const d = C.STICKERS[s.id], tx = stickerTxt(s);
    const inShop = ui.screen === 'shop';
    tipFor = { kind: 'sticker', i };
    openTip('sticker', { i, s, d, tx, inShop });
  }
  function tapeTip(i: number) {
    const id = run!.tapes[i], d = C.TAPES[id]; if (!d) return;
    tipFor = { kind: 'tape', i };
    openTip('tape', { i, id, d, usable: tapeReady(id) });
  }
  // when a tape can be used, in a few words
  function tapeWhen(d: TapeDef) { return d.id === 'royalty' ? t('when you pick a record or in the shop') : t('when you pick a record, for the next song'); }
  function whenText(w: TapeDef['when']) { return w.includes('play') && !w.includes('hand') ? 'Use it while a song is playing.' : w.includes('hand') && !w.includes('play') ? 'Use it while you pick a record.' : 'Use it during a gig.'; }
  function useTape(i: number) {
    const r = C.useTape(run!, i);
    closeTip();
    if (!r) return;
    T('solo_tape', { ...runAt(), id: r.id, screen: ui.screen });
    Sound.sfx('tape'); haptic('light');
    saveRun();
    if (r.fresh) return hand(true);
    if (['fifty', 'second', 'crystal'].includes(r.id) && ui.screen === 'hand') toast(t('{tape}: ready for the next song', { tape: t(C.TAPES[r.id].name) }), 2200, C.TAPES[r.id].icon);
    if (r.id === 'goldpress') { if (ui.screen === 'hand') return hand(); view.sleeves(); return; }
    if (ui.screen === 'play') {
      // keep the clock running: redraw the bits that changed
      const keep = ui.started;
      renderPlay(); ui.started = keep; if (!ui.answered) { stopTimers(); tick(); }
      return;
    }
    if (ui.screen === 'hand') return hand();
    if (ui.screen === 'shop') return shop();
    if (ui.screen === 'result') view.tapeUsed();
  }
  
  // ---------------------------------------------------------------- the end of a run
  // ---------------------------------------------------------------- the moments that make it a game
  // The tour so far, gig by gig: what became of each (cleared, lost), where you are, what's still locked.
  // state: 'play' (this gig is on), 'cleared' (just won it), 'failed' (lost it), 'won' (the tour)
  function tourMap(state = 'play') {
    if (!run) return [];
    const nights: TourNight[] = [];
    const last = run.endless ? run.night + 1 : C.NIGHTS_TO_WIN - 1, first = run.endless ? Math.max(0, run.night - 1) : 0;
    for (let n = first; n <= last; n++) {
      const N = C.nightInfo(n), here = n === run.night;
      const nodes = [0, 1, 2].map((g) => {
        let st = 'locked';
        if (n < run!.night || (here && g < run!.gigNo)) st = 'done';
        else if (here && g === run!.gigNo) st = state === 'failed' ? 'lost' : state === 'cleared' || state === 'won' ? 'done fresh' : 'now';
        else if (state === 'cleared' && ((here && g === run!.gigNo + 1) || (run!.gigNo === 2 && n === run!.night + 1 && g === 0))) st = 'next';
        const ic = st.startsWith('done') ? 'seal-check' : st === 'lost' ? 'x-circle' : g === 2 ? (st === 'locked' ? 'lock' : 'crown') : st === 'locked' ? 'lock' : 'microphone-stage';
        const set = g < 2 && run!.sets ? run!.sets[n * 3 + g] : null, S = set && set !== 'name' && !st.startsWith('locked') ? C.SETS[set] : null;
        return { st, ic, legend: g === 2, label: (g === 2 ? t('Legend') : t('Gig {n}', { n: g + 1 })) + (S ? ' · ' + t(S.name) : ''), color: S ? S.color : null, set: S ? t(S.short) : null };
      });
      nights.push({ n, N, here, past: n < run!.night, nodes });
    }
    return nights;
  }
  // where in the tour this gig is: "Night 1 · The Garage · Gig 2"
  const gigWhere = () => (C.oneNight(run!) ? nightLabel() : t('Night {n} · {venue}', { n: run!.night + 1, venue: t(C.nightInfo(run!.night).name) })) + ' · ' + (run!.gig && run!.gig.legend ? legendName(run!.gig.legend) : gigName(run!.gigNo));
  
  // The end of a gig, the moment it happens: a full screen that says what just happened. Won it: the stamp, the score
  // against the target and the crowd. Lost it: the lights go down, the tour is over, and by how much.
  function verdict(r: GigEnd) {
    if (ui.verdict) return;
    (ui as Complete<ResultUi>).verdict = r;
    const seeded = C.oneNight(run!), g = run!.gig, c = run!.cleared;
    const win = r === 'cleared' || r === 'won', again = r === 'replay';
    const hype = win && c ? c.hype : g ? g.hype : run!.totalHype, target = win && c ? c.target : g ? g.target : 0;
    const text = again ? t('Not this time') : !win ? (g && g.legend && !seeded ? t('Beaten by {name}!', { name: legendName(g.legend) }) : t('Tour over')) : seeded ? (r === 'won' ? t('Night complete!') : t('Gig {n} of {m} done', { n: run!.gigNo + 1, m: 3 })) : r === 'won' ? t('You won the tour!') : c && c.legend ? t('Legend beaten!') : t('Gig cleared!');
    const line = !win ? t('You were {n} points short of {target}.', { n: fmt(Math.max(0, target - hype)), target: fmt(target) })
      : seeded ? t(r === 'won' ? '{n} points tonight' : '{n} points so far tonight', { n: fmt(run!.totalHype) })
      : hype > target ? t('{n} points to spare', { n: fmt(hype - target) }) : t('Right on target');
    const act = r === 'cleared' ? 'to-cleared' : again ? 'to-replay' : 'to-over';
    const btn = again ? t('Play it again') : !win ? t('See your tour') : r === 'cleared' ? (seeded ? t('Next gig') : t('Collect')) : seeded ? t('See your score') : t('Take a bow');
    const pct = target ? Math.min(100, (100 * hype) / target) : 100;
    // Night 1 falling short isn't the end: it says so, and the gig starts again with new songs
    const sub = again ? t('Night 1 gives you another go, with new songs. From Night 2, a missed target ends the tour.') : undefined;
    view.verdict({ win, tour: r === 'won', again, where: gigWhere(), text, seeded, hype, target, line, sub, act, btn, pct });
    Sound.stop(win ? 1.2 : 0.4);
    if (again) { Sound.sfx('scratch'); haptic('error'); return; }
    if (win) { setTimeout(() => { Sound.sfx('fanfare'); Sound.sfx('crowd'); haptic('success'); if (r === 'won' || (c && c.legend)) confetti(140); else confetti(80); }, 420); }
    else { Sound.sfx('scratch'); setTimeout(() => { Sound.sfx('boo'); haptic('error'); }, 450); }
  }
  
  // A gig starting: the stakes, big, for a moment (a tap skips it). Then the records.
  function gigSplash() {
    const g = run!.gig; if (!g || reduced()) return;
    view.splash({ g, seeded: C.oneNight(run!), N: C.nightInfo(run!.night), set: g.set !== 'name' && !g.legend ? setShow(g.set, g.plays) : null, color: fieldColor() });
    Sound.sfx('scratch');
  }
  
  function over(fromVerdict?: boolean) {
    clearTimeout(verdictTimer); view.closeVerdict();
    stopTimers();
    Sound.stop(1.5);
    // daily: a Daily or a challenge (its own board, not the tour's records); oneN: an old one-night one
    const daily = C.seeded(run!), oneN = C.oneNight(run!), isDaily = run!.mode === 'daily', isDuel = run!.mode === 'duel';
    ui = { screen: 'over' };
    // the run's fans, levels and records, once
    let gained = 0, newUnlocks: Unlock[] = [];
    if (!run!.counted) {
      run!.counted = true;
      const before = level();
      gained = C.fansFor(run!);
      meta.fans += gained;
      meta.runs += daily ? 0 : 1;
      if (run!.won && !daily) { meta.wins++; meta.crateWins[run!.crate] = (meta.crateWins[run!.crate] || 0) + 1; meta.stake[run!.crate] = Math.max(meta.stake[run!.crate] ?? -1, run!.stake || 0); }
      if (!daily) { run!.newBest = run!.totalHype > meta.best && meta.runs > 1; meta.best = Math.max(meta.best, run!.totalHype); meta.bestNight = Math.max(meta.bestNight, run!.night + 1); }
      meta.exacts += run!.exacts;
      if (run!.bestSong && (!meta.bestSong || run!.bestSong.hype > meta.bestSong.hype)) meta.bestSong = run!.bestSong;
      const after = level();
      for (let l = before + 1; l <= after; l++) newUnlocks.push(...C.unlocksAt(l).map((u) => ({ ...u, l })));
      run!.gained = gained; run!.unlocks = newUnlocks; run!.levelUp = after > before ? after : 0;
      if (isDuel) {
        rememberDuel(run!.duel!.id, { mineHype: run!.totalHype, played: true });
        postDuel(run!);
      }
      if (isDaily) {
        const D = meta.daily, day = run!.day!;
        // the day before the one played, not before now: a go started at 23:50 and finished after midnight keeps it.
        // A Backstage Pass (Duolingo's streak freeze) covers one missed day; one is earned every 3 days in a row.
        const goesOn = D.last === dayBefore(day) || D.last === day;
        const saved = !goesOn && D.pass && D.last === dayBefore(dayBefore(day));
        if (saved) { D.pass = 0; run!.passSaved = true; }
        D.streak = goesOn || saved ? (D.last === day ? D.streak : D.streak + 1) : 1;
        if (D.streak >= 3 && D.streak % 3 === 0 && !D.pass && D.last !== day) { D.pass = 1; run!.passEarned = true; }
        D.last = day; D.best = Math.max(D.best || 0, run!.totalHype);
        D.days[day] = { hype: run!.totalHype, grid: dailyGrid(), won: run!.won, night: run!.full ? run!.night : undefined, by: meta.uid || null, brag: bragLine() };
        const ahead = keepWeek();
        if (inApp() && meta.remind) toApp({ type: 'daily-done', streak: D.streak, ...tomorrowsGig(ahead) });
        postDaily(run!).then((s) => { if (s) view.standing(standingText(s)); });
        const keys = Object.keys(D.days).sort(); while (keys.length > 60) delete D.days[keys.shift()!];
      }
      saveMeta();
      checkChallenges();
      // Solo earns party looks too: it says so here, or nobody would know. The whole screen, where the page has it, once
      // the results have had their moment and nothing else is open over them; else a toast each.
      const have = progressOf(meta), looks = ITEMS.filter((x) => x.unlock && hasUnlocked(x, have) && !hasUnlocked(x, lookBase));
      lookBase = have; run!.looks = looks.map((x) => [x.slot, x.id]);
      const shown = run!;
      const reveal = () => { if (ui.screen !== 'over' || run !== shown) return; if (view.verdictOpen() || view.tipOpen()) return void setTimeout(reveal, 1500); view.rewards!(shown.looks!); };
      if (looks.length && view.rewards) setTimeout(reveal, 2200);
      else looks.slice(0, 2).forEach((x, i) => setTimeout(() => toast(`${t('New for your character')}: ${t(x.name)}`, 2600, 'gold-record'), 900 + i * 2700));
      T('solo_end', { ...runAt(), won: run!.won, hype: run!.totalHype, songs: run!.plays, rights: run!.rights, stickers: run!.stickers.map((s) => s.id),
        legends: run!.legendsBeaten, fans: gained, level: level(), level_up: run!.levelUp || 0, unlocks: newUnlocks.length, looks: looks.length,
        new_best: !!run!.newBest, streak: isDaily ? meta.daily.streak : 0, endless: run!.endless, signed: !!acct }); // (20 details at most)
      saveRun();
    } else { gained = run!.gained || 0; newUnlocks = run!.unlocks || []; }
    const won = run!.won;
    setField(isDuel ? '#ff5f9e' : won ? '#ffc531' : oneN ? '#ffc531' : '#141432');
    if (!fromVerdict) { if (won) { confetti(120); Sound.sfx('fanfare'); } else if (!oneN) Sound.sfx('boo'); }
    if (!fromVerdict && (run!.levelUp || run!.newBest)) setTimeout(() => Sound.sfx('exact'), won ? 1700 : 900); // something to keep, even from a lost run
    const lv = level(), nxt = C.LEVELS[lv], prv = C.LEVELS[lv - 1] || 0;
    const rights = run!.history.filter((h) => h.r === 'right').length;
    const G = run!.ghost, beat = G && run!.totalHype > G.hype;
    const title = isDuel && G ? (beat ? t('You beat {name}!', { name: G.name || t('the leader') }) : t('{name} wins this one', { name: G.name || t('The leader') })) : isDuel ? t('Score set: now send it') : oneN ? (won ? t('Night complete!') : t('The crowd went home')) : won ? t('You won the tour!') : run!.gig && run!.gig.legend ? t('Beaten by {name}!', { name: legendName(run!.gig.legend) }) : t('Tour over');
    const where = oneN ? nightLabel() : gigWhere();
    // why it ended, in one line: how far short you fell, or what you just did
    const why = !oneN && !won && run!.gig ? t('This gig: {h} of {target}, {n} short.', { h: fmt(run!.gig.hype), n: fmt(Math.max(0, run!.gig.target - run!.gig.hype)), target: fmt(run!.gig.target) })
      : !oneN && won ? t('Five nights, fifteen gigs. The crowd is yours.') : '';
    const gigsDone = run!.gigsCleared || 0;
    // Not signed in, a tour that got somewhere (a gig cleared, something unlocked) offers to keep it: on three tours at
    // most, then the chip in the bar is enough. The Daily's own card asks to make tomorrow count.
    const keepN = +(meta.seen && meta.seen.keepTour) || 0;
    const keepTour = !daily && !isDuel && !acct && googleOn() && (gigsDone >= 1 || newUnlocks.length > 0) && (keepN < 3 || run!.keepShown);
    if (keepTour && !run!.keepShown) { run!.keepShown = 1; meta.seen = { ...(meta.seen || {}), keepTour: keepN + 1 }; saveMeta(); saveRun(); T('signup_shown', { where: 'tour_end', gigs: gigsDone, unlocks: newUnlocks.length }); }
    if (isDaily && !acct && googleOn() && !run!.keepShown) { run!.keepShown = 1; saveRun(); T('signup_shown', { where: 'daily_end' }); }
    // the way on: after the Daily, the tour kept backstage (or a new one); after a tour, today's Daily if it's still open
    const kept = daily ? loadSaved<SavedRun>(RUN_KEY) : null, cont = kept && kept.run && !kept.run.over ? { night: kept.run.night, stickers: kept.run.stickers.length } : null;
    view.draw('over', { won, daily, oneN, isDaily, isDuel, gained, newUnlocks, looks: run!.looks || [], lv, nxt, prv, rights, title, where, why, gigsDone, keepTour: !!keepTour, cont, dailyOpen: !isDaily && !dayGo(today()) });
    if (!daily && !won) dropRun('tour');
    if (daily) setTimeout(refreshBoards, isDuel ? 1200 : 1500); // after the score is in
    // a reminder already set on the web: say so instead of offering it again
    if (isDaily && !inApp() && acct) dailyReminder().then((x) => { if (x && x.on && ui.screen === 'over') view.reminder('on'); }).catch(() => {});
  }
  async function postDuel(r: Run) {
    try {
      const cum = cumOf(r);
      await fetch(api('/solo/duel/' + r.duel!.id + '/score'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sid: deviceId(), token: r.duel!.token || null, name: meta.name || null, hype: r.totalHype, songs: cum.length, rights: r.rights, cum }) });
    } catch (e) {}
  }
  // the run's setlist: every song played, named or not (the ones you missed are worth a listen)
  // a song named for the first time ever (its shelf count is 1): a NEW on its cover, a collection hit at the end
  const setlist = (): SetlistEntry[] => run!.history.filter((x) => x.s).map((x) => ({ ...x, isNew: x.r === 'right' && !!meta.shelf[x.id] && meta.shelf[x.id][4] === 1 }));
  // a cover played back: tap to hear the song, tap again (or anything else) to stop
  let previewId: string | null = null;
  function markPreview(id: string | null) { previewId = id; view.previewing(id); }
  async function preview(id: string) {
    if (previewId === id) { Sound.stop(0.3); return markPreview(null); }
    markPreview(id);
    try { await Sound.play(id, { onEnd: () => { if (previewId === id) markPreview(null); } }); }
    catch (e) { if (previewId === id) markPreview(null); toast(t("That record won't play.")); }
  }
  // the daily's card: a square per song, a row per gig
  function dailyGrid(r: Run = run!) {
    const rows: string[][] = [];
    let gi = 0, n = 0;
    const plays = r.history.filter((h) => h.r !== 'skip');
    const per = C.RULES.plays + r.stickers.reduce((a, s) => a + (C.STICKERS[s.id].plays || 0), 0);
    for (const h of plays) {
      const sq = h.r === 'miss' ? '🟥' : h.bet === 'exact' ? '🎯' : h.t != null && h.t <= 3 ? '🟩' : '🟨';
      // a row a gig, through the whole tour (an old one-night go: three rows; one without gigs: by count)
      const k = h.g != null ? (h.n || 0) * 3 + h.g : Math.min(2, gi);
      (rows[k] = rows[k] || []).push(sq);
      if (++n >= per) { n = 0; gi++; }
    }
    return rows.filter((x) => x && x.length).map((x) => x.join('')).join('\n');
  }
  // the one human detail that makes a shared grid worth a click (Wordle, GeoGuessr): the fastest song, or the years
  function bragLine(r: Run = run!) {
    const rights = r.history.filter((h) => h.r === 'right' && h.t != null && h.s);
    const fast = rights.sort((a, b) => a.t! - b.t!)[0];
    if (fast && (fast.t! <= 3 || !r.exacts)) return '⚡ ' + t('Fastest ear: “{title}” in {s} s', { title: fast.s.title, s: String(fast.t).replace('.', LANG === 'es' ? ',' : '.') });
    if (r.exacts) return '🎯 ' + (r.exacts === 1 ? t('1 year spot on') : t('{n} years spot on', { n: r.exacts }));
    return '';
  }
  function shareDaily() {
    const day = today(), d: Pick<DailyGo, 'standing' | 'brag'> & Partial<Pick<DailyGo, 'won' | 'night'>> & { hype: number | null; grid: string | null } = meta.daily.days[day] || { hype: run && run.totalHype, grid: run && dailyGrid() };
    const st = d.standing && d.standing.n! > 1 ? ' · ' + t('top {p}%', { p: Math.max(1, 100 - Math.round((100 * d.standing.below!) / (d.standing.n! - 1))) }) : '';
    const brag = d.brag || (run && run.mode === 'daily' && run.day === day ? bragLine() : '');
    // how far the tour went, since the grid's rows alone don't say it
    const far = 'won' in d && d.night != null ? (d.won ? '🏆 ' + t('You won the tour!') : t('Night {n} of {m}', { n: d.night + 1, m: C.NIGHTS_TO_WIN })) + '\n' : '';
    const text = `SongBuzz · ${t('Daily Gig #{n}', { n: dayNo(day) })}\n${far}${d.grid}\n${fmt(d.hype!)} ${t('points')}${st}${dailyStreak() > 1 ? ' · ' + t('{n}-day streak', { n: dailyStreak() }) : ''}${brag ? '\n' + brag : ''}`;
    const url = location.origin + '/solo';
    T('solo_share', { day: dayNo(day) });
    if (navigator.share) navigator.share({ title: 'SongBuzz Daily', text, url }).catch(() => {});
    else if (navigator.clipboard) navigator.clipboard.writeText(text + '\n' + url).then(() => toast(t('Copied: paste it anywhere'))).catch(() => toast(text));
    else toast(text, 5000);
  }
  
  // the pause menu
  function pause() {
    // one way on (full width), two ways out side by side, giving up quieter underneath
    openTip('pause', { tour: !!run && !C.seeded(run), today: !!run && run.mode === 'daily' && !C.oneNight(run) });
    pauseOpen = true;
    freeze();
  }
  
  // ---------------------------------------------------------------- taps
  // what a tap on the screen does: name, what it carries (i, id, kind...), and the element when there is one
  function act(name: string, d: ActData = {}, el?: HTMLButtonElement) {
    const i = +d.i!;
    Sound.unlock();
    if (previewId != null && name !== 'preview') { Sound.stop(0.3); markPreview(null); }
    switch (name) {
      case 'preview': return preview(d.id!);
      case 'new': case 'again': {
        // nothing to choose yet (one crate, no harder tours): straight on stage
        const open = Object.values(C.CRATES).filter((c) => c.level <= level() && (!c.needsCountry || SongBuzzEngine.LOCAL[soloCountry()]));
        // the very first tour goes straight in (one crate, nothing to choose yet); after that the crates screen, where the
        // country is chosen too
        if (open.length <= 1 && !Object.keys(meta.stake || {}).length && !meta.runs) return startTour('mixtape', 0);
        return crates();
      }
      case 'continue': { const s = loadSaved<SavedRun>(RUN_KEY); if (!s) return menu(); run = s.run; deck.restore(s.deck); T('solo_continue', runAt()); return resume(); }
      case 'daily': return startDaily();
      case 'crate': return startTour(d.id!, ui.stake || 0);
      case 'stake': (ui as Complete<CratesUi>).stake = i; return crates();
      case 'starter': { const st = run!.starterChoices && run!.starterChoices[i]; if (!st) return; run!.stickers.push(st); delete run!.starterChoices; saveRun(); Sound.sfx('exact'); haptic('success'); T('solo_starter', { id: st.id, mode: run!.mode }); return intro(); }
      case 'country': return setCountry(d.cc!);
      case 'menu': thaw(false); closeTip(); saveRun(); return menu();
      case 'howto': return howto('menu');
      case 'howto-in': if (frozen) { thaw(false); fromHowto = true; } closeTip(); return howto('resume');
      case 'resume': return resume();
      case 'shelf': return shelf();
      case 'profile': return profile();
      case 'retry': return ui.retry && ui.retry();
      case 'go': Sound.sfx('scratch'); seen('intro'); seen('daily'); if (run!.gig) seen('set:' + run!.gig.set); return hand();
      case 'set': {
        const o = C.chooseSet(run!, i); if (!o) return;
        seen('sets'); seen('set:' + o.set); seen('intro'); saveRun();
        Sound.sfx('pick'); haptic('light'); T('solo_set', { set: o.set, prize: o.prize.kind });
        if (o.prize.kind === 'tape') { refreshBar(); toast(t('Tape saved: a button shows up when you can use it.'), 2600, 'cassette-tape'); }
        return hand();
      }
      case 'more': return hearMore();
      case 'pick': if (!(ui.screen === 'hand' || (ui.screen === 'result' && run!.phase === 'hand'))) return; Sound.sfx('pick'); haptic('light'); if (C.pick(run!, i)) { saveRun(); playSong(); } return;
      // The Buzz button covers the answers, and they turn face up on their own after two seconds: a tap meant for Buzz
      // at that moment lands on an answer. For a moment after they turn up, a tap on one is ignored (keys aren't).
      case 'answer': if (performance.now() - (ui.revealedAt || 0) < 450) return; return onAnswer(i);
      case 'buzz': return reveal(true);
      case 'skip': {
        if (ui.screen !== 'play' || ui.answered || !ui.opts || ui.buzzAt != null) return; // buzzed: you're committed
        const cur = run!.gig!.cur!, r = C.skip(run!); if (!r) return;
        ui.answered = true; stopTimers(); Sound.sfx('scratch'); Sound.stop(0.3); rememberRecent(cur.song.id); saveRun();
        view.skipped();
        setTimeout(() => { ui = { screen: 'result', cur }; view.draw('skippedSong', { song: cur.song, hud: hudOf() }); settle(); }, 700);
        return;
      }
      case 'bank': return onBet(null);
      case 'bet': return onBet(d.slot != null ? +d.slot : +d.y!);
      case 'next': return next();
      case 'to-cleared': view.closeVerdict(); return cleared();
      case 'to-hand': return hand();
      case 'to-over': return over(d.from === 'verdict');
      case 'to-replay': clearTimeout(verdictTimer); view.closeVerdict(); return intro();
      case 'shop': return shop();
      // after a night's Legend: stop here; Continue picks the tour up backstage
      case 'stop-night': if (run && run.phase === 'cleared') C.openShop(run); T('solo_stop', runAt()); saveRun(); return menu();
      case 'offer': return offerTip(d.kind as ShopKind, i);
      case 'buy': {
        const it = (d.kind === 'tape' ? run!.shop!.tapes : run!.shop!.items)[i], cash = run!.cash;
        const r = C.buy(run!, d.kind as ShopKind, i);
        if (r.ok && it) T('solo_buy', { ...runAt(), kind: d.kind, id: it.id, price: it.price, cash, foil: !!(it as ShopSticker).foil });
        closeTip();
        if (r.ok) { Sound.sfx('cash'); haptic('success'); saveRun(); shop(); toast(d.kind === 'tape' ? t('Tape saved: a button shows up when you can use it.') : t('{name} is on your case.', { name: (C.STICKERS[(run!.shop!.items[i] || ({} as ShopSticker)).id] ? stickerTxt(run!.shop!.items[i]).name : '') }), 3200); }
        return;
      }
      case 'gift': { const r = C.takeGift(run!, i); if (r.ok) { T('solo_gift', { ...runAt(), i }); Sound.sfx('exact'); haptic('success'); confetti(30); saveRun(); shop(); } return; }
      case 'sell': { const sold = run!.stickers[i], v = C.sell(run!, i); closeTip(); if (v) { T('solo_sell', { ...runAt(), id: sold ? sold.id : '', price: +v || 0 }); Sound.sfx('cash'); saveRun(); shop(); } return; }
      case 'foil': { const res2 = C.buyFoil(run!); if (res2.ok) { T('solo_foil', runAt()); Sound.sfx('exact'); haptic('success'); confetti(30); saveRun(); shop(); toast(t('Foil! That sticker now does ×1.5.'), 3000); } else if (res2.error === 'cash') toast(t('Not enough cash')); return; }
      case 'tally-speed': { meta.fastTally = !meta.fastTally; saveMeta(); view.tallySpeed(); return; }
      case 'reroll': if (C.reroll(run!)) { T('solo_reroll', { ...runAt(), cash: run!.cash }); Sound.sfx('scratch'); saveRun(); shop(); } return;
      case 'leave-shop': seen('shop'); C.leaveShop(run!); saveRun(); return intro();
      case 'tip': return d.kind === 'tape' ? tapeTip(i) : stickerTip(i);
      case 'usetape': return useTape(i);
      case 'tapes': return tapesSheet();
      case 'closetip': return closeTip();
      case 'pause': return pause();
      case 'encore-info': return openTip('encore', {});
      case 'abandon': T('solo_abandon', { ...runAt(), screen: ui.screen, songs: run!.plays }); thaw(false); closeTip(); run!.over = true; run!.phase = 'over'; return over();
      case 'endless': C.goEndless(run!); saveRun(); T('solo_endless', runAt()); return intro();
      case 'share-daily': return shareDaily();
      case 'daily-board': return dailyBoard();
      case 'daily-unranked': return startDaily({ unranked: true });
      case 'account': return accountSheet();
      case 'gsignout': return signOutGoogle();
      case 'gapp': return appSignIn(el);
      case 'duel-new': return createDuel();
      case 'duel-open': return openDuel(d.id!);
      case 'duel-play': return playDuel(d.id!, ui.info || {});
      case 'duel-share': return shareDuel(d.id!, +d.score! || 0);
      case 'remind':
        T('solo_remind', { streak: dailyStreak(), web: inApp() ? 0 : 1 });
        if (inApp()) return toApp({ type: 'remind', streak: dailyStreak(), ...tomorrowsGig(keepWeek()) });
        // on the web: a notification at 7 pm (the browser asks first, from this tap: nothing may wait before it)
        if (pushNeedsPin()) return pinAdd('solo_remind');
        if (typeof Notification !== 'undefined' && Notification.permission === 'denied') return view.reminder('off');
        return void remindDaily(true, 19).then((r) => view.reminder(r && r.on ? 'on' : 'off'));
      case 'board-tab': return view.boardTab(d.k || 'all');
    }
  }
  // a phone put away mid-song: the song and its clock pause; back, the pause menu is waiting
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'hidden') return;
    saveRun();
    if (ui.screen === 'play' && !ui.answered && run && !frozen) pause();
  });
  // a run kept on this device, picked up where it stopped
  function adopt(s: SavedRun) { run = s.run; deck.restore(s.deck); }
  function pausedHere() {
    ui = { screen: 'paused' };
    setField(fieldColor());
    view.draw('paused', {});
  }
  
  return {
    get run() { return run; },
    get ui() { return ui; },
    get meta() { return meta; },
    get acct() { return acct; },
    friendsBoardJson,
    get gcfg() { return gcfg; },
    get previewId() { return previewId; },
    get frozen() { return frozen; },
    refreshBar, toast, confetti, setField, refreshBoards, C, api, getJson, fmt, money, cloud, T, reduced, wait,
    haptic, META_KEY, keyFor, blankMeta, saveMeta, level, loadSaved, saveRun, dropRun, rbClient, account, forget,
    appGoogle, googleOn, soloCall, SYNC_KEY, syncedPart, devicePart, canon, queueSync, syncProgress, redrawAfterSync,
    boardJson, firstName, hasProgress, accountSheet, appSignIn, onGoogle, signOutGoogle, today, dayNo, dayBefore,
    yesterday, untilTomorrow, postDaily, dailyTicket, refreshStanding, standingText, inApp, toApp, passHolds,
    dailyStreak, dayGo, mine, keepStreak, shelfSize, CHALLENGES, checkChallenges, MUTE, Sound, deck, lc, baseTitle,
    shuffleWith, seeded, DECADE_PL, crateSongs, dailySongs, yearAsks, fillYear, fillYears, optionsFor, supply,
    DECADE_COLOR, FAMILY_ICON, decLabel, tv, stickerTxt, shortLabel, tapeReady, readyTapes,
    HINT_ORDER, tapeHintOf, tapesSheet, meterOf, hudOf, COACH, coachLine, seen, RUN_SCREENS, BACK, nightLabel,
    gigName, legendName, dailyLegends, dailyLegend, legendFace, legendColor, LOOK_COLORS, lookAvatar, cumOf,
    ghostLine, rivalMoment, boardFoot, nameAsk, saveName, duels, rememberDuel, duelLink, shareDuel, createDuel,
    openDuel, playDuel, loading, refreshDuels, duelWho, duelStatus, menu, lastNight, soloCountry, countryPicks,
    setCountry, stakeOf, crates, dailyBoard, howto, shelf, profile, decadeStats, unlockInfo, startTour, dailyGate,
    startDaily, playedElsewhere, errorScreen, resume, starterPick, intro, dealUp, DARE_ICON, sleeveLikes,
    specialsDealt, oneNightColor, fieldColor, setColor, setShow, offersShow, momentShow, askLine, moreKey, hearMore, momentSplash, hand, medleyOn, slotOpts, lastSong, refill, stopTimers, freeze, thaw, playSong, renderPlay,
    hiddenOpts, reveal, tick, onAnswer, cur0Edition, addToShelf, countDecade, rememberRecent, result, fmtMult,
    hudBefore, hurryTally, BET_SECS, betClock, betPanel, betOdds, slotLine, timelinePanel, onBet, betDone, settle,
    encoreFlash, next, cleared, shop, offerTip, openTip, closeTip, stickerTip, tapeTip, tapeWhen, whenText, useTape,
    tourMap, gigWhere, verdict, gigSplash, over, postDuel, setlist, markPreview, preview, dailyGrid, bragLine,
    shareDaily, pause, act, adopt, pausedHere, RUN_KEY, DAILY_KEY, DUEL_KEY,
  };
}
  